import React, { Component, useMemo, useState } from 'react';
import { gql } from 'apollo-boost';
import { useQuery } from '@apollo/react-hooks';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Input,
  Label,
  Row
} from 'reactstrap';
import { useEntitlements } from '../services/entitlementsService';
import { UpgradeHint } from '../components/SharedComponents/featureLock';

const PRESET_ROWS = [
  [
    { key: 'today', label: 'Today' },
    { key: 'this_week', label: 'This Week' },
    { key: 'this_month', label: 'This Month' },
    { key: 'this_quarter', label: 'This Quarter' },
    { key: 'this_year', label: 'This Year' },
    { key: 'all_time', label: 'All Time' }
  ],
  [
    { key: 'yesterday', label: 'Yesterday' },
    { key: 'last_week', label: 'Last Week' },
    { key: 'last_month', label: 'Last Month' },
    { key: 'last_quarter', label: 'Last Quarter' },
    { key: 'last_year', label: 'Last Year' }
  ]
];

class MetricsPageErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, errorMessage: '' };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, errorMessage: error && error.message ? error.message : 'Metrics failed to render.' };
  }

  componentDidCatch(error) {
    if (typeof console !== 'undefined' && console.error) {
      console.error('Metrics page render error:', error);
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="content">
          <Row>
            <Col md="12">
              <Card>
                <CardBody>
                  <h4 className="mb-2">Metrics temporarily unavailable</h4>
                  <p className="mb-0">{this.state.errorMessage}</p>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      );
    }

    return this.props.children;
  }
}

const GET_COMPLETED_SHIPMENTS_BY_TYPE = gql`
  query GetCompletedShipmentsByType($completed_from: timestamptz!, $completed_to: timestamptz!) {
    Shipments(
      where: {
        _and: [
          { deleted_at: { _is_null: true } }
          { status: { _eq: "shipment_completed" } }
          { shipping_fee: { _neq: 0.01 } }
          { description: { _nilike: "%Test%" } }
          {
            _or: [
              {
                _and: [
                  { completed_at: { _is_null: false } }
                  { completed_at: { _gte: $completed_from } }
                  { completed_at: { _lte: $completed_to } }
                ]
              }
              {
                _and: [
                  { completed_at: { _is_null: true } }
                  { updated_at: { _gte: $completed_from } }
                  { updated_at: { _lte: $completed_to } }
                ]
              }
            ]
          }
        ]
      }
      order_by: { completed_at: desc }
    ) {
      shipment_id
      created_at
      scheduled_for
      is_expedited
      shipment_type
      vehicle_type
      cust_auth0_id
      customer_blob
      shipping_fee
      shipping_fee_blob
      completed_at
      updated_at
    }
  }
`;

const GET_COMPLETED_SHIPMENT_DRIVERS = gql`
  query GetCompletedShipmentDrivers($completed_from: timestamptz!, $completed_to: timestamptz!) {
    Routes(
      where: {
        _and: [
          { deleted_at: { _is_null: true } }
          {
            Shipments: {
              _and: [
                { deleted_at: { _is_null: true } }
                { status: { _eq: "shipment_completed" } }
                { shipping_fee: { _neq: 0.01 } }
                {
                  _or: [
                    {
                      _and: [
                        { completed_at: { _is_null: false } }
                        { completed_at: { _gte: $completed_from } }
                        { completed_at: { _lte: $completed_to } }
                      ]
                    }
                    {
                      _and: [
                        { completed_at: { _is_null: true } }
                        { updated_at: { _gte: $completed_from } }
                        { updated_at: { _lte: $completed_to } }
                      ]
                    }
                  ]
                }
              ]
            }
          }
        ]
      }
      order_by: { updated_at: desc }
    ) {
      route_id
      RouteDrivers(where: { deleted_at: { _is_null: true } }, order_by: { created_at: asc }) {
        driver_auth0_id
        driver_id
        driver_blob
      }
    }
  }
`;

const GET_COMPLETED_SHIPMENT_DETAILS = gql`
  query GetCompletedShipmentDetails($completed_from: timestamptz!, $completed_to: timestamptz!) {
    Shipments(
      where: {
        _and: [
          { deleted_at: { _is_null: true } }
          { status: { _eq: "shipment_completed" } }
          { shipping_fee: { _neq: 0.01 } }
          { description: { _nilike: "%Test%" } }
          {
            _or: [
              {
                _and: [
                  { completed_at: { _is_null: false } }
                  { completed_at: { _gte: $completed_from } }
                  { completed_at: { _lte: $completed_to } }
                ]
              }
              {
                _and: [
                  { completed_at: { _is_null: true } }
                  { updated_at: { _gte: $completed_from } }
                  { updated_at: { _lte: $completed_to } }
                ]
              }
            ]
          }
        ]
      }
      order_by: { completed_at: desc }
    ) {
      shipment_id
      created_at
      scheduled_for
      is_expedited
      distance
      weight
      shipping_fee_blob
      completed_at
      updated_at
      Orders {
        order_id
        created_at
        updated_at
        actual_action
        actions
        estimated_arrival_time
        completed_at
      }
    }
  }
`;

const GET_COMPLETED_SHIPMENT_PICKUP_EVENTS = gql`
  query GetCompletedShipmentPickupEvents($completed_from: timestamptz!, $completed_to: timestamptz!) {
    Shipments(
      where: {
        _and: [
          { deleted_at: { _is_null: true } }
          { status: { _eq: "shipment_completed" } }
          { shipping_fee: { _neq: 0.01 } }
          { description: { _nilike: "%Test%" } }
          {
            _or: [
              {
                _and: [
                  { completed_at: { _is_null: false } }
                  { completed_at: { _gte: $completed_from } }
                  { completed_at: { _lte: $completed_to } }
                ]
              }
              {
                _and: [
                  { completed_at: { _is_null: true } }
                  { updated_at: { _gte: $completed_from } }
                  { updated_at: { _lte: $completed_to } }
                ]
              }
            ]
          }
        ]
      }
      order_by: { completed_at: desc }
    ) {
      shipment_id
      created_at
      scheduled_for
      is_expedited
      Orders {
        order_id
        created_at
        updated_at
        actual_action
        actions
        arrived_at
        estimated_arrival_time
        completed_at
      }
      Route {
        path
      }
    }
  }
`;

const GET_COMPLETED_SHIPMENT_PAYOUTS = gql`
  query GetCompletedShipmentPayouts($completed_from: timestamptz!, $completed_to: timestamptz!) {
    Shipments(
      where: {
        _and: [
          { deleted_at: { _is_null: true } }
          { status: { _eq: "shipment_completed" } }
          { shipping_fee: { _neq: 0.01 } }
          { description: { _nilike: "%Test%" } }
          {
            _or: [
              {
                _and: [
                  { completed_at: { _is_null: false } }
                  { completed_at: { _gte: $completed_from } }
                  { completed_at: { _lte: $completed_to } }
                ]
              }
              {
                _and: [
                  { completed_at: { _is_null: true } }
                  { updated_at: { _gte: $completed_from } }
                  { updated_at: { _lte: $completed_to } }
                ]
              }
            ]
          }
        ]
      }
      order_by: { completed_at: desc }
    ) {
      shipment_id
      shipping_fee_blob
    }
  }
`;

const GET_COMPLETED_SHIPMENTS_AGGREGATE = gql`
  query GetCompletedShipmentsAggregate($completed_from: timestamptz!, $completed_to: timestamptz!) {
    Shipments_aggregate(
      where: {
        _and: [
          { deleted_at: { _is_null: true } }
          { status: { _eq: "shipment_completed" } }
          { shipping_fee: { _neq: 0.01 } }
          { description: { _nilike: "%Test%" } }
          {
            _or: [
              {
                _and: [
                  { completed_at: { _is_null: false } }
                  { completed_at: { _gte: $completed_from } }
                  { completed_at: { _lte: $completed_to } }
                ]
              }
              {
                _and: [
                  { completed_at: { _is_null: true } }
                  { updated_at: { _gte: $completed_from } }
                  { updated_at: { _lte: $completed_to } }
                ]
              }
            ]
          }
        ]
      }
    ) {
      aggregate {
        count
        sum {
          shipping_fee
        }
        min {
          completed_at
          updated_at
        }
      }
    }
  }
`;

const GET_GOPHR_ACCOUNTS_CREATED = gql`
  query GetGophrAccountsCreated($created_from: timestamptz!, $created_to: timestamptz!) {
    Users(
      where: {
        _and: [
          { created_at: { _gte: $created_from } }
          { created_at: { _lte: $created_to } }
        ]
      }
      order_by: { created_at: desc }
    ) {
      auth0_id
      created_at
      Roles {
        role
        role_description
      }
    }
  }
`;

const GET_GOPHR_ACCOUNTS_CREATED_CAMEL = gql`
  query GetGophrAccountsCreatedCamel($created_from: timestamptz!, $created_to: timestamptz!) {
    Users(
      where: {
        _and: [
          { createdAt: { _gte: $created_from } }
          { createdAt: { _lte: $created_to } }
        ]
      }
      order_by: { createdAt: desc }
    ) {
      auth0_id
      createdAt
      Roles {
        role
        role_description
      }
    }
  }
`;

const createDate = value => new Date(value.getTime());

const startOfDay = value => {
  const date = createDate(value);
  date.setHours(0, 0, 0, 0);
  return date;
};

const endOfDay = value => {
  const date = createDate(value);
  date.setHours(23, 59, 59, 999);
  return date;
};

const addDays = (value, days) => {
  const date = createDate(value);
  date.setDate(date.getDate() + days);
  return date;
};

const startOfWeek = value => {
  const date = startOfDay(value);
  const dayOffset = (date.getDay() + 6) % 7;
  return addDays(date, -dayOffset);
};

const endOfWeek = value => endOfDay(addDays(startOfWeek(value), 6));

const startOfMonth = value => {
  const date = createDate(value);
  date.setDate(1);
  date.setHours(0, 0, 0, 0);
  return date;
};

const endOfMonth = value => {
  const date = createDate(value);
  date.setMonth(date.getMonth() + 1, 0);
  date.setHours(23, 59, 59, 999);
  return date;
};

const startOfQuarter = value => {
  const date = createDate(value);
  const quarterStartMonth = Math.floor(date.getMonth() / 3) * 3;
  date.setMonth(quarterStartMonth, 1);
  date.setHours(0, 0, 0, 0);
  return date;
};

const endOfQuarter = value => {
  const date = startOfQuarter(value);
  date.setMonth(date.getMonth() + 3, 0);
  date.setHours(23, 59, 59, 999);
  return date;
};

const startOfYear = value => {
  const date = createDate(value);
  date.setMonth(0, 1);
  date.setHours(0, 0, 0, 0);
  return date;
};

const endOfYear = value => {
  const date = createDate(value);
  date.setMonth(11, 31);
  date.setHours(23, 59, 59, 999);
  return date;
};

const addMonths = (value, months) => {
  const date = createDate(value);
  date.setMonth(date.getMonth() + months);
  return date;
};

const padNumber = value => String(value).padStart(2, '0');

const toDateInputValue = value => (
  value.getFullYear() + '-' + padNumber(value.getMonth() + 1) + '-' + padNumber(value.getDate())
);

const toMonthInputValue = value => (
  value.getFullYear() + '-' + padNumber(value.getMonth() + 1)
);

const parseDateInputValue = value => {
  if (!value) {
    return null;
  }

  const parsed = new Date(value + 'T00:00:00');
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return parsed;
};

const parseMonthInputValue = value => {
  if (!value) {
    return null;
  }

  const parsed = new Date(value + '-01T00:00:00');
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return parsed;
};

const formatDateTime = value => value.toLocaleString();

const formatCurrency = value => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return '$0.00';
  }

  return numericValue.toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

const TYPE_COLORS = ['#6f9d35', '#9ac85d', '#b7d98b', '#82c6ce', '#bfe4ea', '#f1994b', '#c7d2fe'];

const normalizeKey = value => String(value)
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '_')
  .replace(/_+/g, '_')
  .replace(/^_+|_+$/g, '');

const normalizeShipmentType = value => {
  if (!value || typeof value !== 'string') {
    return 'unknown';
  }

  const normalized = normalizeKey(value);
  return normalized || 'unknown';
};

const VEHICLE_TYPE_ALIASES = {
  car: 'car',
  suv: 'suv',
  truck: 'truck',
  pickup: 'truck',
  pickup_truck: 'truck',
  heavy_duty_truck: 'heavy_duty_truck',
  one_ton_pickup: 'heavy_duty_truck',
  sprinter_van: 'sprinter_van',
  trailer: 'trailer',
  bumper_pull: 'trailer',
  bumper_pull_trailer: 'trailer',
  enclosed_trailer: 'enclosed_trailer',
  box_truck: 'box_truck',
  car_hauler: 'car_hauler',
  gooseneck: 'gooseneck_trailer',
  gooseneck_trailer: 'gooseneck_trailer',
  flatbed: 'trailer',
  '18_wheeler_flatbed': '18_wheeler_flat_bed',
  '18_wheeler_flat_bed': '18_wheeler_flat_bed',
  '18_wheeler_dryvan': '18_wheeler_dry_van',
  '18_wheeler_dry_van': '18_wheeler_dry_van',
  '18_wheeler_reefer': '18_wheeler_reefer'
};

const normalizeVehicleType = value => {
  if (!value || typeof value !== 'string') {
    return 'unknown_vehicle';
  }

  const normalizedKey = normalizeKey(value);

  return VEHICLE_TYPE_ALIASES[normalizedKey] || normalizedKey;
};

const toShipmentTypeLabel = value => (
  normalizeShipmentType(value)
    .split('_')
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
);

const toVehicleTypeLabel = value => normalizeVehicleType(value);

const formatCount = value => Number(value || 0).toLocaleString();

const formatPercent = value => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return '0.00%';
  }

  return numericValue.toFixed(2) + '%';
};

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const ON_TIME_DELIVERY_GRACE_MINUTES = 15;
const ON_TIME_PICKUP_EXPEDITED_HOURS = 1;
const ON_TIME_PICKUP_STANDARD_HOURS = 4;
const ON_TIME_PICKUP_SCHEDULED_WINDOW_HOURS = 2;

const buildDayOfWeekRows = shipments => {
  const rows = WEEKDAY_LABELS.map((label, dayIndex) => ({
    key: label.toLowerCase(),
    label,
    dayIndex,
    count: 0,
    revenue: 0
  }));

  shipments.forEach(shipment => {
    const completedDate = getShipmentCompletedDate(shipment);
    if (!completedDate) {
      return;
    }

    const dayIndex = completedDate.getDay();
    if (typeof dayIndex !== 'number' || dayIndex < 0 || dayIndex > 6) {
      return;
    }

    const feeValue = Number(shipment && shipment.shipping_fee);
    const safeFee = Number.isFinite(feeValue) ? feeValue : 0;

    rows[dayIndex].count += 1;
    rows[dayIndex].revenue += safeFee;
  });

  return rows;
};

const HOUR_LABELS = [
  '12 AM', '1 AM', '2 AM', '3 AM', '4 AM', '5 AM',
  '6 AM', '7 AM', '8 AM', '9 AM', '10 AM', '11 AM',
  '12 PM', '1 PM', '2 PM', '3 PM', '4 PM', '5 PM',
  '6 PM', '7 PM', '8 PM', '9 PM', '10 PM', '11 PM'
];

const buildHourOfDayRows = (shipments, mode) => {
  const rows = HOUR_LABELS.map((label, hour) => ({
    key: 'hour-' + hour,
    label,
    hour,
    count: 0
  }));

  shipments.forEach(shipment => {
    const rawValue = mode === 'created'
      ? shipment && shipment.created_at
      : (shipment && (shipment.completed_at || shipment.updated_at));

    if (!rawValue) {
      return;
    }

    const parsed = new Date(rawValue);
    if (Number.isNaN(parsed.getTime())) {
      return;
    }

    const hour = parsed.getHours();
    if (typeof hour !== 'number' || hour < 0 || hour > 23) {
      return;
    }

    rows[hour].count += 1;
  });

  return rows;
};

const buildBreakdown = (shipments, mode) => {
  const totalsByType = shipments.reduce((accumulator, shipment) => {
    const shipmentType = normalizeShipmentType(shipment && shipment.shipment_type);
    const feeValue = Number(shipment && shipment.shipping_fee);
    const safeFee = Number.isFinite(feeValue) ? feeValue : 0;

    if (!accumulator[shipmentType]) {
      accumulator[shipmentType] = { rawType: shipmentType, count: 0, revenue: 0 };
    }

    accumulator[shipmentType].count += 1;
    accumulator[shipmentType].revenue += safeFee;
    return accumulator;
  }, {});

  const rows = Object.keys(totalsByType)
    .map(type => totalsByType[type])
    .sort((a, b) => {
      if (mode === 'count') {
        return b.count - a.count;
      }
      return b.revenue - a.revenue;
    });

  const totalValue = rows.reduce((sum, row) => sum + (mode === 'count' ? row.count : row.revenue), 0);

  return rows.map((row, index) => {
    const value = mode === 'count' ? row.count : row.revenue;
    const percent = totalValue > 0 ? (value / totalValue) * 100 : 0;
    return {
      key: row.rawType,
      label: toShipmentTypeLabel(row.rawType),
      rawType: row.rawType,
      value,
      percent,
      color: TYPE_COLORS[index % TYPE_COLORS.length]
    };
  });
};

const buildVehicleBreakdown = (shipments, mode) => {
  const totalsByType = shipments.reduce((accumulator, shipment) => {
    const vehicleType = normalizeVehicleType(shipment && shipment.vehicle_type);
    const feeValue = Number(shipment && shipment.shipping_fee);
    const safeFee = Number.isFinite(feeValue) ? feeValue : 0;

    if (!accumulator[vehicleType]) {
      accumulator[vehicleType] = { rawType: vehicleType, count: 0, revenue: 0 };
    }

    accumulator[vehicleType].count += 1;
    accumulator[vehicleType].revenue += safeFee;
    return accumulator;
  }, {});

  const rows = Object.keys(totalsByType)
    .map(type => totalsByType[type])
    .sort((a, b) => {
      if (mode === 'count') {
        return b.count - a.count;
      }
      return b.revenue - a.revenue;
    });

  const totalValue = rows.reduce((sum, row) => sum + (mode === 'count' ? row.count : row.revenue), 0);

  return rows.map((row, index) => {
    const value = mode === 'count' ? row.count : row.revenue;
    const percent = totalValue > 0 ? (value / totalValue) * 100 : 0;

    return {
      key: row.rawType,
      label: toVehicleTypeLabel(row.rawType),
      rawType: row.rawType,
      value,
      percent,
      color: TYPE_COLORS[index % TYPE_COLORS.length]
    };
  });
};

const getUniqueCustomerKey = shipment => {
  if (!shipment || typeof shipment !== 'object') {
    return '';
  }

  if (shipment.cust_auth0_id && String(shipment.cust_auth0_id).trim()) {
    return String(shipment.cust_auth0_id).trim();
  }

  const rawBlob = shipment.customer_blob;
  let blob = {};

  if (rawBlob && typeof rawBlob === 'object') {
    blob = rawBlob;
  } else if (rawBlob && typeof rawBlob === 'string') {
    try {
      const parsed = JSON.parse(rawBlob);
      blob = parsed && typeof parsed === 'object' ? parsed : {};
    } catch (error) {
      blob = {};
    }
  }

  const blobAuth0Id = blob.auth0_id || blob.user_auth0_id || blob.cust_auth0_id || blob.customer_auth0_id;
  if (blobAuth0Id && String(blobAuth0Id).trim()) {
    return String(blobAuth0Id).trim();
  }

  if (blob.email && String(blob.email).trim()) {
    return String(blob.email).trim().toLowerCase();
  }
  if (blob.phone && String(blob.phone).trim()) {
    return String(blob.phone).trim();
  }

  const fullName = [blob.first_name, blob.last_name]
    .map(value => (value ? String(value).trim() : ''))
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  if (fullName) {
    return fullName;
  }

  return shipment.shipment_id ? 'shipment-' + String(shipment.shipment_id) : '';
};

const getUniqueDriverKeyFromRouteDriver = routeDriver => {
  if (!routeDriver || typeof routeDriver !== 'object') {
    return '';
  }

  if (routeDriver.driver_auth0_id && String(routeDriver.driver_auth0_id).trim()) {
    return String(routeDriver.driver_auth0_id).trim();
  }

  if (routeDriver.driver_id && String(routeDriver.driver_id).trim()) {
    return String(routeDriver.driver_id).trim();
  }

  const driverBlob = routeDriver.driver_blob && typeof routeDriver.driver_blob === 'object'
    ? routeDriver.driver_blob
    : {};

  if (driverBlob.auth0_id && String(driverBlob.auth0_id).trim()) {
    return String(driverBlob.auth0_id).trim();
  }

  if (driverBlob.driver_auth0_id && String(driverBlob.driver_auth0_id).trim()) {
    return String(driverBlob.driver_auth0_id).trim();
  }

  if (driverBlob.driver_id && String(driverBlob.driver_id).trim()) {
    return String(driverBlob.driver_id).trim();
  }

  if (driverBlob.email && String(driverBlob.email).trim()) {
    return String(driverBlob.email).trim().toLowerCase();
  }

  return '';
};

const resolveAccountType = userRecord => {
  const roles = userRecord && Array.isArray(userRecord.Roles) ? userRecord.Roles : [];

  const hasDriverRole = roles.some(roleRecord => {
    const roleDescription = String(roleRecord && roleRecord.role_description ? roleRecord.role_description : '').toLowerCase();
    const roleCode = Number(roleRecord && roleRecord.role);
    return roleDescription.includes('driver') || (Number.isFinite(roleCode) && roleCode >= 300 && roleCode < 400);
  });

  if (hasDriverRole) {
    return 'Driver';
  }

  const hasBusinessRole = roles.some(roleRecord => {
    const roleDescription = String(roleRecord && roleRecord.role_description ? roleRecord.role_description : '').toLowerCase();
    const roleCode = Number(roleRecord && roleRecord.role);
    return (
      roleDescription.includes('merchant') ||
      roleDescription.includes('store') ||
      roleDescription.includes('inventory') ||
      roleDescription.includes('picker') ||
      (Number.isFinite(roleCode) && roleCode >= 200 && roleCode < 300)
    );
  });

  if (hasBusinessRole) {
    return 'Business';
  }

  return 'Individual';
};

const buildAccountTypeBreakdown = users => {
  const counts = users.reduce((accumulator, userRecord) => {
    const accountType = resolveAccountType(userRecord);
    accumulator[accountType] = (accumulator[accountType] || 0) + 1;
    return accumulator;
  }, { Driver: 0, Individual: 0, Business: 0 });

  const rows = [
    { key: 'Driver', label: 'Driver', value: counts.Driver || 0, color: '#82c6ce' },
    { key: 'Individual', label: 'Individual', value: counts.Individual || 0, color: '#b7d98b' },
    { key: 'Business', label: 'Business', value: counts.Business || 0, color: '#6f9d35' }
  ];

  const totalValue = rows.reduce((sum, item) => sum + item.value, 0);

  return rows.map(item => ({
    ...item,
    percent: totalValue > 0 ? (item.value / totalValue) * 100 : 0
  }));
};

const parseNumericMetric = value => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
};

const parseTimestampMs = value => {
  if (!value) {
    return null;
  }

  const parsedMs = new Date(value).getTime();
  return Number.isFinite(parsedMs) ? parsedMs : null;
};

const parseMoneyAmount = value => {
  if (value == null) {
    return null;
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === 'string') {
    const normalized = value.replace(/[^0-9.-]/g, '');
    const numericValue = Number(normalized);
    return Number.isFinite(numericValue) ? numericValue : null;
  }

  return null;
};

const parseDriverPayFromShippingFeeBlob = value => {
  let blob = {};

  if (value && typeof value === 'object') {
    blob = value;
  } else if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      blob = parsed && typeof parsed === 'object' ? parsed : {};
    } catch (error) {
      blob = {};
    }
  }

  const driverEarning = blob && blob.driverEarning && typeof blob.driverEarning === 'object'
    ? blob.driverEarning
    : {};
  const shipmentBlob = blob && blob.shipment && typeof blob.shipment === 'object'
    ? blob.shipment
    : {};
  const shipmentDriverEarning = shipmentBlob && shipmentBlob.driverEarning && typeof shipmentBlob.driverEarning === 'object'
    ? shipmentBlob.driverEarning
    : {};

  const directCandidates = [
    driverEarning.driver_1099_earning,
    driverEarning.total_earning,
    shipmentDriverEarning.driver_1099_earning,
    shipmentDriverEarning.total_earning,
    blob.driver_1099_earning,
    blob.total_earning,
    blob.driverPay,
    blob.driver_pay,
    shipmentBlob.driverPay,
    shipmentBlob.driver_pay
  ];

  for (let index = 0; index < directCandidates.length; index += 1) {
    const numericValue = parseMoneyAmount(directCandidates[index]);
    if (numericValue != null) {
      return numericValue;
    }
  }

  const componentFields = [
    'distanceEarning_1099',
    'expeditedEarning_1099',
    'weightEarning',
    'setup_earning',
    'bonus',
    'minDriverPay_1099'
  ];

  const componentFieldsSnake = [
    'distance_earning',
    'expedited_earning',
    'overweight_earning',
    'setup_earning',
    'tools_earning',
    'bonus',
    'min_pay'
  ];

  const sumFromSource = (source, fields) => fields.reduce((sum, fieldName) => {
    const numericValue = parseMoneyAmount(source && source[fieldName]);
    return sum + (numericValue != null ? numericValue : 0);
  }, 0);

  const camelSum = sumFromSource(driverEarning, componentFields);
  if (camelSum > 0) {
    return camelSum;
  }

  const shipmentCamelSum = sumFromSource(shipmentDriverEarning, componentFields);
  if (shipmentCamelSum > 0) {
    return shipmentCamelSum;
  }

  const snakeSum = sumFromSource(driverEarning, componentFieldsSnake);
  if (snakeSum > 0) {
    return snakeSum;
  }

  const shipmentSnakeSum = sumFromSource(shipmentDriverEarning, componentFieldsSnake);
  if (shipmentSnakeSum > 0) {
    return shipmentSnakeSum;
  }

  return 0;
};

const parseExpeditedFlag = value => {
  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return normalized === 'true' || normalized === 't' || normalized === '1' || normalized === 'yes';
  }

  if (typeof value === 'number') {
    return value === 1;
  }

  return false;
};

const parseRoutePath = shipment => {
  const routePathRaw = shipment && shipment.Route ? shipment.Route.path : null;

  if (Array.isArray(routePathRaw)) {
    return routePathRaw;
  }

  if (typeof routePathRaw !== 'string') {
    return [];
  }

  try {
    const parsedPath = JSON.parse(routePathRaw);
    return Array.isArray(parsedPath) ? parsedPath : [];
  } catch (error) {
    return [];
  }
};

const getRouteWaypointForOrder = (shipment, order) => {
  if (!order || typeof order !== 'object') {
    return null;
  }

  return parseRoutePath(shipment).find(waypoint => {
    if (!waypoint || typeof waypoint !== 'object') {
      return false;
    }

    if (order.order_id && (waypoint.order_id === order.order_id || waypoint.waypoint_id === order.order_id)) {
      return true;
    }

    return false;
  }) || null;
};

const getWaypointArrivalMs = waypoint => {
  if (!waypoint || typeof waypoint !== 'object') {
    return null;
  }

  return (
    parseTimestampMs(waypoint.arrived_at)
    || parseTimestampMs(waypoint.arrivedAt)
    || parseTimestampMs(waypoint.arrival_validation && waypoint.arrival_validation.timestamp)
  );
};

const getOrderArrivalEvidenceMs = (shipment, order) => {
  const actions = order && order.actions && typeof order.actions === 'object' ? order.actions : null;
  const matchedWaypoint = getRouteWaypointForOrder(shipment, order);

  return (
    parseTimestampMs(order && order.arrived_at)
    || parseTimestampMs(actions && actions.arrival_validation && actions.arrival_validation.timestamp)
    || parseTimestampMs(actions && (actions.arrived_at || actions.arrivedAt || actions.driver_arrived_at || actions.driver_arrivedAt))
    || getWaypointArrivalMs(matchedWaypoint)
  );
};

const getOrderEtaMs = order => {
  const actions = order && order.actions && typeof order.actions === 'object' ? order.actions : null;

  return (
    parseTimestampMs(order && order.estimated_arrival_time)
    || parseTimestampMs(order && order.estimatedArrivalTime)
    || parseTimestampMs(actions && (actions.estimated_arrival_time || actions.estimatedArrivalTime || actions.eta || actions.dropoff_eta || actions.drop_off_eta))
  );
};

const buildExpeditedBreakdown = shipments => {
  const expeditedCount = shipments.reduce((sum, shipment) => sum + (parseExpeditedFlag(shipment && shipment.is_expedited) ? 1 : 0), 0);
  const standardCount = Math.max(0, shipments.length - expeditedCount);
  const totalValue = standardCount + expeditedCount;

  return [
    {
      key: 'standard_shipments',
      label: 'Standard Shipments',
      value: standardCount,
      percent: totalValue > 0 ? (standardCount / totalValue) * 100 : 0,
      color: '#f2a766'
    },
    {
      key: 'expedited_shipments',
      label: 'Expedited Shipments',
      value: expeditedCount,
      percent: totalValue > 0 ? (expeditedCount / totalValue) * 100 : 0,
      color: '#9acb4a'
    }
  ];
};

const buildDonutBackground = breakdownItems => {
  if (!breakdownItems || breakdownItems.length === 0) {
    return '#1c2f3f';
  }

  const segments = [];
  let cursor = 0;
  breakdownItems.forEach(item => {
    const next = cursor + item.percent;
    segments.push(item.color + ' ' + cursor + '% ' + next + '%');
    cursor = next;
  });

  if (cursor < 100) {
    segments.push('#1c2f3f ' + cursor + '% 100%');
  }

  return 'conic-gradient(' + segments.join(', ') + ')';
};

const ShipmentTypeDonutCard = ({ title, totalDisplay, breakdownItems, valueFormatter }) => {
  const [hoveredSegmentKey, setHoveredSegmentKey] = useState(null);
  const donutBackground = buildDonutBackground(breakdownItems);
  const donutSize = 260;
  const donutOuterRadius = donutSize / 2;
  const donutInnerRadius = donutSize * 0.55 / 2;

  const segmentRanges = useMemo(() => {
    let cursor = 0;
    return breakdownItems.map(item => {
      const start = cursor;
      const end = cursor + item.percent;
      cursor = end;
      return {
        key: item.key,
        start,
        end
      };
    });
  }, [breakdownItems]);

  const handleDonutMouseMove = event => {
    if (!event || !event.currentTarget || segmentRanges.length === 0) {
      setHoveredSegmentKey(null);
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const deltaX = event.clientX - centerX;
    const deltaY = event.clientY - centerY;
    const distance = Math.sqrt((deltaX * deltaX) + (deltaY * deltaY));

    if (distance < donutInnerRadius || distance > donutOuterRadius) {
      setHoveredSegmentKey(null);
      return;
    }

    const cartesianDegrees = (Math.atan2(deltaY, deltaX) * 180 / Math.PI + 360) % 360;
    const conicDegrees = (cartesianDegrees + 90) % 360;
    const conicPercent = (conicDegrees / 360) * 100;

    const matchedSegment = segmentRanges.find(segment => conicPercent >= segment.start && conicPercent < segment.end);
    setHoveredSegmentKey(matchedSegment ? matchedSegment.key : null);
  };

  return (
    <Card className="mb-0">
      <CardHeader>
        <h5 className="title mb-0">{title}</h5>
      </CardHeader>
      <CardBody>
        <Row>
          <Col md="5" sm="12" className="mb-3 mb-md-0">
            {breakdownItems.length === 0 ? (
              <p className="mb-0" style={{ color: 'rgba(255, 255, 255, 0.9)' }}>No completed shipments in selected range.</p>
            ) : (
              <div>
                {breakdownItems.map(item => (
                  <div
                    key={item.key}
                    className="d-flex align-items-center justify-content-between"
                    style={{
                      marginBottom: '0.45rem',
                      padding: '0.22rem 0.35rem',
                      borderRadius: '6px',
                      backgroundColor: hoveredSegmentKey === item.key ? 'rgba(255, 255, 255, 0.12)' : 'transparent',
                      transition: 'background-color 120ms ease'
                    }}
                    onMouseEnter={() => setHoveredSegmentKey(item.key)}
                    onMouseLeave={() => setHoveredSegmentKey(null)}
                  >
                    <div className="d-flex align-items-center" style={{ gap: '0.45rem' }}>
                      <span style={{ width: '9px', height: '9px', borderRadius: '50%', backgroundColor: item.color, display: 'inline-block' }} />
                      <span style={{ fontSize: '0.88rem', color: 'rgba(255, 255, 255, 0.92)' }}>{item.label}</span>
                    </div>
                    <span style={{ fontSize: '0.88rem', color: 'rgba(255, 255, 255, 0.95)' }}>
                      {valueFormatter(item.value)} ({item.percent.toFixed(2)}%)
                    </span>
                  </div>
                ))}
              </div>
            )}
          </Col>
          <Col md="7" sm="12" className="d-flex justify-content-center align-items-center">
            <div
              style={{ position: 'relative', width: '260px', height: '260px', cursor: 'pointer' }}
              onMouseMove={handleDonutMouseMove}
              onMouseLeave={() => setHoveredSegmentKey(null)}
            >
              <div style={{ width: '100%', height: '100%', borderRadius: '50%', background: donutBackground }} />
              <div
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: '55%',
                  height: '55%',
                  borderRadius: '50%',
                  background: '#0b1f2f',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  textAlign: 'center',
                  padding: '0.4rem'
                }}
              >
                <strong style={{ fontSize: '1.9rem', lineHeight: 1 }}>{totalDisplay}</strong>
                <span style={{ fontSize: '0.95rem' }}>Total</span>
              </div>
            </div>
          </Col>
        </Row>
      </CardBody>
    </Card>
  );
};

const resolvePresetRange = (presetKey, now) => {
  if (presetKey === 'today') {
    return { start: startOfDay(now), end: endOfDay(now), label: 'Today' };
  }

  if (presetKey === 'yesterday') {
    const yesterday = addDays(now, -1);
    return { start: startOfDay(yesterday), end: endOfDay(yesterday), label: 'Yesterday' };
  }

  if (presetKey === 'this_week') {
    return { start: startOfWeek(now), end: endOfWeek(now), label: 'This Week' };
  }

  if (presetKey === 'this_month') {
    return { start: startOfMonth(now), end: endOfMonth(now), label: 'This Month' };
  }

  if (presetKey === 'this_quarter') {
    return { start: startOfQuarter(now), end: endOfQuarter(now), label: 'This Quarter' };
  }

  if (presetKey === 'this_year') {
    return { start: startOfYear(now), end: endOfYear(now), label: 'This Year' };
  }

  if (presetKey === 'all_time') {
    return { start: new Date('2019-11-01T00:00:00.000Z'), end: endOfDay(now), label: 'All Time' };
  }

  if (presetKey === 'last_week') {
    const previousWeekDate = addDays(now, -7);
    return { start: startOfWeek(previousWeekDate), end: endOfWeek(previousWeekDate), label: 'Last Week' };
  }

  if (presetKey === 'last_month') {
    const previousMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    return { start: startOfMonth(previousMonthDate), end: endOfMonth(previousMonthDate), label: 'Last Month' };
  }

  if (presetKey === 'last_quarter') {
    const previousQuarterDate = new Date(now.getFullYear(), now.getMonth() - 3, 1);
    return { start: startOfQuarter(previousQuarterDate), end: endOfQuarter(previousQuarterDate), label: 'Last Quarter' };
  }

  const previousYearDate = new Date(now.getFullYear() - 1, 0, 1);
  return { start: startOfYear(previousYearDate), end: endOfYear(previousYearDate), label: 'Last Year' };
};

const MetricCard = ({ title, value, hint }) => (
  <Card className="mb-0">
    <CardBody className="text-center">
      <p className="mb-1" style={{ fontSize: '0.8rem', color: 'rgba(255, 255, 255, 0.9)' }}>{title}</p>
      <h3 className="mb-1">{value}</h3>
      <p className="mb-0" style={{ fontSize: '0.75rem', color: 'rgba(255, 255, 255, 0.86)' }}>{hint}</p>
    </CardBody>
  </Card>
);

const GaugeMetricCard = ({ title, valueDisplay, unitLabel, currentValue, minValue, maxValue, ranges, tickValues, angleOffset = 0 }) => {
  const safeMin = Number.isFinite(minValue) ? minValue : 0;
  const safeMax = Number.isFinite(maxValue) && maxValue > safeMin ? maxValue : safeMin + 1;
  const boundedValue = Math.max(safeMin, Math.min(Number.isFinite(currentValue) ? currentValue : safeMin, safeMax));
  const normalizedValue = (boundedValue - safeMin) / (safeMax - safeMin);
  const angle = (-130 + angleOffset + (normalizedValue * 260)) * (Math.PI / 180);
  const centerX = 150;
  const centerY = 124;
  const radius = 88;

  const polarToCartesian = (cx, cy, r, angleDegrees) => {
    const radians = angleDegrees * (Math.PI / 180);
    return {
      x: cx + (r * Math.cos(radians)),
      y: cy + (r * Math.sin(radians))
    };
  };

  const describeArc = (cx, cy, r, startAngle, endAngle) => {
    const start = polarToCartesian(cx, cy, r, endAngle);
    const end = polarToCartesian(cx, cy, r, startAngle);
    const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';

    return ['M', start.x, start.y, 'A', r, r, 0, largeArcFlag, 0, end.x, end.y].join(' ');
  };

  const pointerTipX = centerX + (radius - 10) * Math.cos(angle);
  const pointerTipY = centerY + (radius - 10) * Math.sin(angle);
  const pointerLeftX = centerX + 7 * Math.cos(angle + (Math.PI / 2));
  const pointerLeftY = centerY + 7 * Math.sin(angle + (Math.PI / 2));
  const pointerRightX = centerX + 7 * Math.cos(angle - (Math.PI / 2));
  const pointerRightY = centerY + 7 * Math.sin(angle - (Math.PI / 2));

  return (
    <Card className="mb-0">
      <CardHeader>
        <h5 className="title mb-0">{title}</h5>
      </CardHeader>
      <CardBody>
        <div style={{ position: 'relative', width: '100%', maxWidth: '300px', margin: '0 auto' }}>
          <svg width="100%" viewBox="0 0 300 220" role="img" aria-label={title}>
            {ranges.map(range => (
              <path
                key={range.key}
                d={describeArc(centerX, centerY, radius, range.startAngle + angleOffset, range.endAngle + angleOffset)}
                fill="none"
                stroke={range.color}
                strokeWidth="28"
                strokeLinecap="butt"
              />
            ))}

            {tickValues.map(tick => {
              const safeTickValue = Math.max(safeMin, Math.min(tick.value, safeMax));
              const tickRatio = (safeTickValue - safeMin) / (safeMax - safeMin);
              const tickAngle = -130 + angleOffset + (tickRatio * 260);
              const tickPoint = polarToCartesian(centerX, centerY, radius + 24, tickAngle);

              return (
                <text
                  key={tick.label}
                  x={tickPoint.x}
                  y={tickPoint.y}
                  textAnchor="middle"
                  fill="rgba(255, 255, 255, 0.92)"
                  style={{ fontSize: '12px' }}
                >
                  {tick.label}
                </text>
              );
            })}

            <polygon
              points={pointerLeftX + ',' + pointerLeftY + ' ' + pointerTipX + ',' + pointerTipY + ' ' + pointerRightX + ',' + pointerRightY}
              fill="#f6f1e6"
              stroke="#1c2f3f"
              strokeWidth="2"
            />
            <circle cx={centerX} cy={centerY} r="6" fill="#f6f1e6" stroke="#1c2f3f" strokeWidth="2" />

            <rect x={centerX - 74} y={centerY + 22} width="148" height="36" rx="8" fill="rgba(11, 31, 47, 0.92)" />

            <text x={centerX} y={centerY + 45} textAnchor="middle" fill="#f5f5f5" style={{ fontSize: '18px', fontWeight: 700 }}>
              {valueDisplay}
            </text>
            {unitLabel && (
              <text x={centerX} y={centerY + 62} textAnchor="middle" fill="rgba(255, 255, 255, 0.9)" style={{ fontSize: '11px' }}>
                {unitLabel}
              </text>
            )}
          </svg>
        </div>
      </CardBody>
    </Card>
  );
};

const DayOfWeekBarChartCard = ({ title, rows, valueKey, color, valueFormatter, axisLabel }) => {
  const chartWidth = 560;
  const chartHeight = 250;
  const paddingLeft = 46;
  const paddingRight = 16;
  const paddingTop = 20;
  const paddingBottom = 48;
  const plotWidth = chartWidth - paddingLeft - paddingRight;
  const plotHeight = chartHeight - paddingTop - paddingBottom;

  const getNiceAxisMax = value => {
    const safeValue = Number.isFinite(value) && value > 0 ? value : 1;
    const magnitude = Math.pow(10, Math.floor(Math.log10(safeValue)));
    const scaled = safeValue / magnitude;

    let niceScale = 1;
    if (scaled <= 1) {
      niceScale = 1;
    } else if (scaled <= 2) {
      niceScale = 2;
    } else if (scaled <= 5) {
      niceScale = 5;
    } else {
      niceScale = 10;
    }

    return niceScale * magnitude;
  };

  const maxValue = getNiceAxisMax(Math.max(1, ...rows.map(row => Number(row[valueKey]) || 0)));
  const bandWidth = rows.length > 0 ? plotWidth / rows.length : plotWidth;
  const barWidth = Math.max(22, bandWidth * 0.66);
  const axisTickRatios = [0, 0.5, 1];

  return (
    <Card className="mb-0">
      <CardHeader>
        <h5 className="title mb-0">{title}</h5>
      </CardHeader>
      <CardBody>
        <svg width="100%" viewBox={'0 0 ' + chartWidth + ' ' + chartHeight} role="img" aria-label={title}>
          {axisTickRatios.map(tickRatio => {
            const y = paddingTop + plotHeight - (tickRatio * plotHeight);
            const tickValue = maxValue * tickRatio;

            return (
              <g key={'tick-' + tickRatio}>
                <line
                  x1={paddingLeft}
                  y1={y}
                  x2={chartWidth - paddingRight}
                  y2={y}
                  stroke="rgba(255, 255, 255, 0.12)"
                  strokeWidth="1"
                />
                <text
                  x={paddingLeft - 8}
                  y={y + 4}
                  textAnchor="end"
                  fill="rgba(255, 255, 255, 0.92)"
                  style={{ fontSize: '11px' }}
                >
                  {valueFormatter(tickValue)}
                </text>
              </g>
            );
          })}

          <text
            x={16}
            y={paddingTop + (plotHeight / 2)}
            transform={'rotate(-90 16 ' + (paddingTop + (plotHeight / 2)) + ')'}
            textAnchor="middle"
            fill="rgba(255, 255, 255, 0.9)"
            style={{ fontSize: '11px' }}
          >
            {axisLabel}
          </text>

          {rows.map((row, index) => {
            const value = Number(row[valueKey]) || 0;
            const x = paddingLeft + (index * bandWidth) + ((bandWidth - barWidth) / 2);
            const height = (value / maxValue) * plotHeight;
            const y = paddingTop + plotHeight - height;

            return (
              <g key={row.key}>
                <rect
                  x={x}
                  y={y}
                  width={barWidth}
                  height={Math.max(1, height)}
                  fill={color}
                  rx="2"
                />
                <text
                  x={x + (barWidth / 2)}
                  y={Math.max(12, y - 6)}
                  textAnchor="middle"
                  fill="rgba(255, 255, 255, 0.95)"
                  style={{ fontSize: '11px', fontWeight: 600 }}
                >
                  {valueFormatter(value)}
                </text>
                <text
                  x={x + (barWidth / 2)}
                  y={paddingTop + plotHeight + 18}
                  textAnchor="middle"
                  fill="rgba(255, 255, 255, 0.93)"
                  style={{ fontSize: '11px' }}
                >
                  {row.label}
                </text>
              </g>
            );
          })}
        </svg>
      </CardBody>
    </Card>
  );
};

const TimeOfDayLineChartCard = ({ title, rows, color, xAxisLabel }) => {
  const [hoveredPoint, setHoveredPoint] = useState(null);
  const chartWidth = 560;
  const chartHeight = 250;
  const paddingLeft = 46;
  const paddingRight = 16;
  const paddingTop = 20;
  const paddingBottom = 48;
  const plotWidth = chartWidth - paddingLeft - paddingRight;
  const plotHeight = chartHeight - paddingTop - paddingBottom;

  const getNiceAxisMax = value => {
    const safeValue = Number.isFinite(value) && value > 0 ? value : 1;
    const magnitude = Math.pow(10, Math.floor(Math.log10(safeValue)));
    const scaled = safeValue / magnitude;

    let niceScale = 1;
    if (scaled <= 1) {
      niceScale = 1;
    } else if (scaled <= 2) {
      niceScale = 2;
    } else if (scaled <= 5) {
      niceScale = 5;
    } else {
      niceScale = 10;
    }

    return niceScale * magnitude;
  };

  const maxValue = getNiceAxisMax(Math.max(1, ...rows.map(row => row.count || 0)));
  const stepX = rows.length > 1 ? plotWidth / (rows.length - 1) : plotWidth;
  const axisTickRatios = [0, 0.5, 1];
  const xTickHours = [0, 5, 10, 15, 20];

  const linePath = rows.map((row, index) => {
    const x = paddingLeft + (index * stepX);
    const y = paddingTop + plotHeight - ((row.count / maxValue) * plotHeight);
    return (index === 0 ? 'M' : 'L') + x.toFixed(2) + ' ' + y.toFixed(2);
  }).join(' ');

  const updateHoveredPoint = (event, row, index) => {
    if (!event || !event.currentTarget || !event.currentTarget.ownerSVGElement) {
      return;
    }

    const svgRect = event.currentTarget.ownerSVGElement.getBoundingClientRect();
    const localX = event.clientX - svgRect.left;
    const localY = event.clientY - svgRect.top;

    setHoveredPoint({
      row,
      x: Math.max(10, Math.min(localX + 12, chartWidth - 140)),
      y: Math.max(10, Math.min(localY - 18, chartHeight - 56)),
      index
    });
  };

  return (
    <Card className="mb-0">
      <CardHeader>
        <h5 className="title mb-0">{title}</h5>
      </CardHeader>
      <CardBody>
        <div style={{ position: 'relative' }}>
          <svg width="100%" viewBox={'0 0 ' + chartWidth + ' ' + chartHeight} role="img" aria-label={title}>
            {axisTickRatios.map(tickRatio => {
              const y = paddingTop + plotHeight - (tickRatio * plotHeight);
              const tickValue = maxValue * tickRatio;

              return (
                <g key={'tick-' + tickRatio}>
                  <line
                    x1={paddingLeft}
                    y1={y}
                    x2={chartWidth - paddingRight}
                    y2={y}
                    stroke="rgba(255, 255, 255, 0.12)"
                    strokeWidth="1"
                  />
                  <text
                    x={paddingLeft - 8}
                    y={y + 4}
                    textAnchor="end"
                    fill="rgba(255, 255, 255, 0.92)"
                    style={{ fontSize: '11px' }}
                  >
                    {formatCount(tickValue)}
                  </text>
                </g>
              );
            })}

            <text
              x={16}
              y={paddingTop + (plotHeight / 2)}
              transform={'rotate(-90 16 ' + (paddingTop + (plotHeight / 2)) + ')'}
              textAnchor="middle"
              fill="rgba(255, 255, 255, 0.9)"
              style={{ fontSize: '11px' }}
            >
              Count
            </text>

            <path
              d={linePath}
              fill={color.replace('1)', '0.22)').replace('0.9)', '0.22)').replace('0.85)', '0.22)')}
              stroke="none"
            />
            <path d={linePath} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />

            {rows.map((row, index) => {
              const x = paddingLeft + (index * stepX);
              const y = paddingTop + plotHeight - ((row.count / maxValue) * plotHeight);

              return (
                <circle
                  key={row.key}
                  cx={x}
                  cy={y}
                  r={hoveredPoint && hoveredPoint.row.key === row.key ? '4.2' : '2.8'}
                  fill="#dfe8ef"
                  stroke={color}
                  strokeWidth="1.4"
                  onMouseMove={event => updateHoveredPoint(event, row, index)}
                  onMouseEnter={event => updateHoveredPoint(event, row, index)}
                  onMouseLeave={() => setHoveredPoint(null)}
                />
              );
            })}

            {rows.map((row, index) => {
              const x = paddingLeft + (index * stepX) - (stepX / 2);
              return (
                <rect
                  key={row.key + '-hover-zone'}
                  x={index === 0 ? paddingLeft - (stepX * 0.35) : x}
                  y={paddingTop}
                  width={Math.max(8, stepX * 0.9)}
                  height={plotHeight}
                  fill="transparent"
                  onMouseMove={event => updateHoveredPoint(event, row, index)}
                  onMouseEnter={event => updateHoveredPoint(event, row, index)}
                  onMouseLeave={() => setHoveredPoint(null)}
                />
              );
            })}

            {rows.map((row, index) => {
              if (row.count <= 0 || (index % 3 !== 0 && index !== rows.length - 1)) {
                return null;
              }

              const x = paddingLeft + (index * stepX);
              const y = paddingTop + plotHeight - ((row.count / maxValue) * plotHeight);

              return (
                <text
                  key={row.key + '-label'}
                  x={x}
                  y={Math.max(12, y - 8)}
                  textAnchor="middle"
                  fill="rgba(255, 255, 255, 0.95)"
                  style={{ fontSize: '11px', fontWeight: 600 }}
                >
                  {formatCount(row.count)}
                </text>
              );
            })}

            {xTickHours.map(hour => {
              const x = paddingLeft + (hour * stepX);
              return (
                <text
                  key={'hour-tick-' + hour}
                  x={x}
                  y={paddingTop + plotHeight + 18}
                  textAnchor="middle"
                  fill="rgba(255, 255, 255, 0.93)"
                  style={{ fontSize: '11px' }}
                >
                  {HOUR_LABELS[hour]}
                </text>
              );
            })}

            <text
              x={paddingLeft + (plotWidth / 2)}
              y={chartHeight - 6}
              textAnchor="middle"
              fill="rgba(255, 255, 255, 0.9)"
              style={{ fontSize: '11px' }}
            >
              {xAxisLabel}
            </text>
          </svg>

          {hoveredPoint && (
            <div
              style={{
                position: 'absolute',
                left: hoveredPoint.x + 'px',
                top: hoveredPoint.y + 'px',
                background: 'rgba(11, 31, 47, 0.96)',
                border: '1px solid rgba(255, 255, 255, 0.12)',
                borderRadius: '8px',
                padding: '0.42rem 0.56rem',
                pointerEvents: 'none',
                minWidth: '112px',
                boxShadow: '0 10px 24px rgba(0, 0, 0, 0.28)',
                zIndex: 3
              }}
            >
              <div style={{ fontSize: '0.72rem', color: 'rgba(255, 255, 255, 0.9)', marginBottom: '0.15rem' }}>
                {hoveredPoint.row.label}
              </div>
              <div style={{ fontSize: '0.82rem', lineHeight: 1.35 }}>
                Count: {formatCount(hoveredPoint.row.count)}
              </div>
            </div>
          )}
        </div>
      </CardBody>
    </Card>
  );
};

const getShipmentCompletedDate = shipment => {
  const rawValue = shipment && (shipment.completed_at || shipment.updated_at);
  if (!rawValue) {
    return null;
  }

  const parsed = new Date(rawValue);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return parsed;
};

const getPeriodStart = (value, granularity) => {
  if (granularity === 'week') {
    return startOfWeek(value);
  }
  if (granularity === 'month') {
    return startOfMonth(value);
  }
  return startOfQuarter(value);
};

const getPeriodEnd = (value, granularity) => {
  if (granularity === 'week') {
    return endOfWeek(value);
  }
  if (granularity === 'month') {
    return endOfMonth(value);
  }
  return endOfQuarter(value);
};

const incrementPeriod = (value, granularity) => {
  if (granularity === 'week') {
    return addDays(value, 7);
  }
  if (granularity === 'month') {
    return addMonths(value, 1);
  }
  return addMonths(value, 3);
};

const formatPeriodLabel = (value, granularity) => {
  if (granularity === 'week') {
    return value.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }
  if (granularity === 'month') {
    return value.toLocaleDateString(undefined, { month: 'short', year: '2-digit' });
  }

  const quarter = Math.floor(value.getMonth() / 3) + 1;
  const shortYear = String(value.getFullYear()).slice(-2);
  return 'Q' + quarter + ' ' + shortYear;
};

const buildTimelineSeries = (shipments, rangeStart, rangeEnd, granularity) => {
  if (!rangeStart || !rangeEnd) {
    return [];
  }

  const alignedStart = getPeriodStart(rangeStart, granularity);
  const periods = [];
  let cursor = alignedStart;
  let safety = 0;

  while (cursor <= rangeEnd && safety < 600) {
    const periodStart = createDate(cursor);
    const periodEnd = getPeriodEnd(periodStart, granularity);
    periods.push({
      key: periodStart.toISOString(),
      label: formatPeriodLabel(periodStart, granularity),
      periodStart,
      periodEnd,
      shipments: 0,
      revenue: 0
    });

    cursor = incrementPeriod(periodStart, granularity);
    safety += 1;
  }

  const periodIndexByKey = periods.reduce((accumulator, period, index) => {
    accumulator[period.key] = index;
    return accumulator;
  }, {});

  shipments.forEach(shipment => {
    const completedDate = getShipmentCompletedDate(shipment);
    if (!completedDate || completedDate < rangeStart || completedDate > rangeEnd) {
      return;
    }

    const bucketStart = getPeriodStart(completedDate, granularity);
    const key = bucketStart.toISOString();
    const periodIndex = periodIndexByKey[key];
    if (typeof periodIndex !== 'number') {
      return;
    }

    const feeValue = Number(shipment && shipment.shipping_fee);
    const safeFee = Number.isFinite(feeValue) ? feeValue : 0;
    periods[periodIndex].shipments += 1;
    periods[periodIndex].revenue += safeFee;
  });

  return periods;
};

const resolveTrendRangeStart = (rangeStart, rangeEnd, minimumGranularity) => {
  if (!rangeStart || !rangeEnd) {
    return null;
  }

  if (minimumGranularity === 'week') {
    const minimumStart = startOfWeek(addDays(rangeEnd, -6));
    return rangeStart > minimumStart ? minimumStart : rangeStart;
  }

  const minimumYearStart = startOfYear(rangeEnd);
  return rangeStart > minimumYearStart ? minimumYearStart : rangeStart;
};

const resolveTrendRangeEnd = (rangeStart, rangeEnd, minimumGranularity) => {
  if (!rangeStart || !rangeEnd) {
    return null;
  }

  if (minimumGranularity === 'week') {
    return rangeEnd;
  }

  const minimumYearStart = startOfYear(rangeEnd);
  return rangeStart > minimumYearStart ? endOfYear(rangeEnd) : rangeEnd;
};

const resolveEarliestShipmentDate = aggregateData => {
  const aggregate = aggregateData
    && aggregateData.Shipments_aggregate
    && aggregateData.Shipments_aggregate.aggregate
      ? aggregateData.Shipments_aggregate.aggregate
      : null;

  const candidates = [
    aggregate && aggregate.min ? aggregate.min.completed_at : null,
    aggregate && aggregate.min ? aggregate.min.updated_at : null
  ].filter(Boolean);

  if (candidates.length === 0) {
    return null;
  }

  const dates = candidates
    .map(value => new Date(value))
    .filter(value => !Number.isNaN(value.getTime()))
    .sort((a, b) => a.getTime() - b.getTime());

  return dates.length > 0 ? dates[0] : null;
};

const TimelineChartCard = ({ title, series, loading, valueLabel = 'Revenue' }) => {
  const [hoveredPoint, setHoveredPoint] = useState(null);
  const chartWidth = 380;
  const chartHeight = 196;
  const paddingLeft = 36;
  const paddingRight = 36;
  const paddingTop = 10;
  const paddingBottom = 30;
  const plotWidth = chartWidth - paddingLeft - paddingRight;
  const plotHeight = chartHeight - paddingTop - paddingBottom;

  const getNiceAxisMax = value => {
    const safeValue = Number.isFinite(value) && value > 0 ? value : 1;
    const magnitude = Math.pow(10, Math.floor(Math.log10(safeValue)));
    const scaled = safeValue / magnitude;

    let niceScale = 1;
    if (scaled <= 1) {
      niceScale = 1;
    } else if (scaled <= 2) {
      niceScale = 2;
    } else if (scaled <= 5) {
      niceScale = 5;
    } else {
      niceScale = 10;
    }

    return niceScale * magnitude;
  };

  const formatCompactCurrency = value => {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) {
      return '$0';
    }

    if (Math.abs(numericValue) >= 1000) {
      const abbreviated = (numericValue / 1000).toFixed(0);
      return '$' + abbreviated + 'k';
    }

    return '$' + Math.round(numericValue).toLocaleString();
  };

  const maxShipments = getNiceAxisMax(Math.max(1, ...series.map(item => item.shipments)));
  const maxRevenue = getNiceAxisMax(Math.max(1, ...series.map(item => item.revenue)));
  const stepX = series.length > 0 ? plotWidth / series.length : plotWidth;
  const barWidth = Math.max(5, stepX * 0.62);

  const axisTicks = [0, 0.25, 0.5, 0.75, 1];

  const linePoints = series.map((item, index) => {
    const x = paddingLeft + (index * stepX) + (stepX / 2);
    const y = paddingTop + plotHeight - ((item.shipments / maxShipments) * plotHeight);
    return x.toFixed(2) + ',' + y.toFixed(2);
  }).join(' ');

  const linePath = series.map((item, index) => {
    const x = paddingLeft + (index * stepX) + (stepX / 2);
    const y = paddingTop + plotHeight - ((item.shipments / maxShipments) * plotHeight);
    return (index === 0 ? 'M' : 'L') + x.toFixed(2) + ' ' + y.toFixed(2);
  }).join(' ');

  const latestPoint = series.length > 0 ? series[series.length - 1] : null;

  const updateHoveredPoint = (event, item, index) => {
    if (!event || !event.currentTarget || !event.currentTarget.ownerSVGElement) {
      return;
    }

    const svgRect = event.currentTarget.ownerSVGElement.getBoundingClientRect();
    const localX = event.clientX - svgRect.left;
    const localY = event.clientY - svgRect.top;
    const pointX = paddingLeft + (index * stepX) + (stepX / 2);
    const pointY = paddingTop + plotHeight - ((item.shipments / maxShipments) * plotHeight);

    setHoveredPoint({
      item,
      x: Math.max(12, Math.min(localX + 12, chartWidth - 120)),
      y: Math.max(12, Math.min(localY - 18, chartHeight - 68)),
      pointX,
      pointY
    });
  };

  return (
    <Card className="mb-0">
      <CardHeader>
        <h5 className="title mb-1">{title}</h5>
        <p className="mb-0" style={{ fontSize: '0.75rem', color: 'rgba(255, 255, 255, 0.9)' }}>{valueLabel + ' (bars) and shipments (line)'}</p>
      </CardHeader>
      <CardBody>
        {loading ? (
          <p className="mb-0" style={{ color: 'rgba(255, 255, 255, 0.9)' }}>Loading...</p>
        ) : series.length === 0 ? (
          <p className="mb-0" style={{ color: 'rgba(255, 255, 255, 0.9)' }}>No completed shipments in selected range.</p>
        ) : (
          <div style={{ position: 'relative' }}>
            <svg width="100%" viewBox={'0 0 ' + chartWidth + ' ' + chartHeight} role="img" aria-label={title}>
              {axisTicks.map(tickRatio => {
                const y = paddingTop + plotHeight - (tickRatio * plotHeight);
                return (
                  <line
                    key={tickRatio}
                    x1={paddingLeft}
                    y1={y}
                    x2={chartWidth - paddingRight}
                    y2={y}
                    stroke="rgba(255, 255, 255, 0.12)"
                    strokeWidth="1"
                  />
                );
              })}

              {axisTicks.map(tickRatio => {
                const y = paddingTop + plotHeight - (tickRatio * plotHeight);
                const shipmentsTickValue = Math.round(maxShipments * tickRatio);
                const revenueTickValue = maxRevenue * tickRatio;

                return (
                  <g key={'label-' + tickRatio}>
                    <text
                      x={paddingLeft - 6}
                      y={y + 4}
                      textAnchor="end"
                      fill="rgba(255, 255, 255, 0.93)"
                      style={{ fontSize: '10px' }}
                    >
                      {formatCount(shipmentsTickValue)}
                    </text>
                    <text
                      x={chartWidth - paddingRight + 6}
                      y={y + 4}
                      textAnchor="start"
                      fill="rgba(255, 255, 255, 0.93)"
                      style={{ fontSize: '10px' }}
                    >
                      {formatCompactCurrency(revenueTickValue)}
                    </text>
                  </g>
                );
              })}

              <text
                x={10}
                y={paddingTop + (plotHeight / 2)}
                transform={'rotate(-90 10 ' + (paddingTop + (plotHeight / 2)) + ')'}
                textAnchor="middle"
                fill="rgba(255, 255, 255, 0.9)"
                style={{ fontSize: '10px' }}
              >
                Shipments
              </text>

              <text
                x={chartWidth - 10}
                y={paddingTop + (plotHeight / 2)}
                transform={'rotate(90 ' + (chartWidth - 10) + ' ' + (paddingTop + (plotHeight / 2)) + ')'}
                textAnchor="middle"
                fill="rgba(255, 255, 255, 0.9)"
                style={{ fontSize: '10px' }}
              >
                {valueLabel}
              </text>

              {series.map((item, index) => {
                const x = paddingLeft + (index * stepX) + ((stepX - barWidth) / 2);
                const height = (item.revenue / maxRevenue) * plotHeight;
                const y = paddingTop + plotHeight - height;

                return (
                  <rect
                    key={item.key}
                    x={x}
                    y={y}
                    width={barWidth}
                    height={Math.max(1, height)}
                    fill="rgba(143, 200, 79, 0.88)"
                    rx="2"
                  />
                );
              })}

              {series.map((item, index) => {
                const x = paddingLeft + (index * stepX);

                return (
                  <rect
                    key={item.key + '-hover-zone'}
                    x={x}
                    y={paddingTop}
                    width={Math.max(1, stepX)}
                    height={plotHeight}
                    fill="transparent"
                    onMouseMove={event => updateHoveredPoint(event, item, index)}
                    onMouseEnter={event => updateHoveredPoint(event, item, index)}
                    onMouseLeave={() => setHoveredPoint(null)}
                  />
                );
              })}

              <path
                d={linePath}
                fill="none"
                stroke="#8ad4df"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />

              <polyline
                points={linePoints}
                fill="none"
                stroke="rgba(138, 212, 223, 0.22)"
                strokeWidth="6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />

              {series.map((item, index) => {
                const pointX = paddingLeft + (index * stepX) + (stepX / 2);
                const pointY = paddingTop + plotHeight - ((item.shipments / maxShipments) * plotHeight);

                return (
                  <circle
                    key={item.key + '-point'}
                    cx={pointX}
                    cy={pointY}
                    r={hoveredPoint && hoveredPoint.item.key === item.key ? 4 : 2.5}
                    fill="#8ad4df"
                    stroke="#233344"
                    strokeWidth="1"
                    onMouseMove={event => updateHoveredPoint(event, item, index)}
                    onMouseEnter={event => updateHoveredPoint(event, item, index)}
                    onMouseLeave={() => setHoveredPoint(null)}
                  />
                );
              })}

              {series.length > 0 && (
                <text
                  x={paddingLeft + (plotWidth / 2)}
                  y={chartHeight - 6}
                  textAnchor="middle"
                  fill="rgba(255, 255, 255, 0.9)"
                  style={{ fontSize: '11px' }}
                >
                  {series[0].label + ' - ' + series[series.length - 1].label}
                </text>
              )}
            </svg>

            {hoveredPoint && (
              <div
                style={{
                  position: 'absolute',
                  left: hoveredPoint.x + 'px',
                  top: hoveredPoint.y + 'px',
                  background: 'rgba(11, 31, 47, 0.96)',
                  border: '1px solid rgba(255, 255, 255, 0.12)',
                  borderRadius: '8px',
                  padding: '0.45rem 0.55rem',
                  pointerEvents: 'none',
                  minWidth: '110px',
                  boxShadow: '0 10px 24px rgba(0, 0, 0, 0.28)',
                  zIndex: 2
                }}
              >
                <div style={{ fontSize: '0.72rem', color: 'rgba(255, 255, 255, 0.9)', marginBottom: '0.15rem' }}>
                  {hoveredPoint.item.label}
                </div>
                <div style={{ fontSize: '0.82rem', lineHeight: 1.35 }}>
                  {valueLabel}: {formatCurrency(hoveredPoint.item.revenue)}
                </div>
                <div style={{ fontSize: '0.82rem', lineHeight: 1.35 }}>
                  Shipments: {formatCount(hoveredPoint.item.shipments)}
                </div>
              </div>
            )}

            {latestPoint && (
              <div className="d-flex justify-content-between mt-2" style={{ fontSize: '0.8rem' }}>
                <span style={{ color: 'rgba(255, 255, 255, 0.9)' }}>Latest: {latestPoint.label}</span>
                <span>
                  {formatCount(latestPoint.shipments)} shipments / {formatCurrency(latestPoint.revenue)}
                </span>
              </div>
            )}
          </div>
        )}
      </CardBody>
    </Card>
  );
};

const MetricsContent = ({ role }) => {
  const roleNumber = Number(role);
  const isCustomerView = roleNumber !== 0 && roleNumber !== 100;
  const valueLabel = isCustomerView ? 'Fee' : 'Revenue';
  const [filterMode, setFilterMode] = useState('preset');
  // Default to the current month, not all_time: all_time pulls ~9k+ completed shipments across
  // several network-only queries at once, which can hang/OOM the tab (especially navigating in
  // from the heavy dispatch dashboard). Users can still select All Time explicitly.
  const [selectedPreset, setSelectedPreset] = useState('this_month');
  const [selectedMonth, setSelectedMonth] = useState(toMonthInputValue(new Date()));
  const [rangeStart, setRangeStart] = useState(toDateInputValue(new Date()));
  const [rangeEnd, setRangeEnd] = useState(toDateInputValue(new Date()));

  const activeRange = useMemo(() => {
    const now = new Date();

    if (filterMode === 'preset') {
      return {
        mode: 'preset',
        isValid: true,
        ...resolvePresetRange(selectedPreset, now)
      };
    }

    if (filterMode === 'month') {
      const monthDate = parseMonthInputValue(selectedMonth);

      if (!monthDate) {
        return {
          mode: 'month',
          isValid: false,
          label: 'Month',
          start: null,
          end: null
        };
      }

      return {
        mode: 'month',
        isValid: true,
        label: 'Month: ' + monthDate.toLocaleDateString(undefined, { month: 'long', year: 'numeric' }),
        start: startOfMonth(monthDate),
        end: endOfMonth(monthDate)
      };
    }

    const startDate = parseDateInputValue(rangeStart);
    const endDate = parseDateInputValue(rangeEnd);

    if (!startDate || !endDate || startDate.getTime() > endDate.getTime()) {
      return {
        mode: 'range',
        isValid: false,
        label: 'Custom Range',
        start: null,
        end: null
      };
    }

    return {
      mode: 'range',
      isValid: true,
      label: 'Custom Range',
      start: startOfDay(startDate),
      end: endOfDay(endDate)
    };
  }, [filterMode, rangeEnd, rangeStart, selectedMonth, selectedPreset]);

  const shipmentQueryVariables = useMemo(() => {
    if (!activeRange.isValid || !activeRange.start || !activeRange.end) {
      return null;
    }

    return {
      completed_from: activeRange.start.toISOString(),
      completed_to: activeRange.end.toISOString()
    };
  }, [activeRange]);

  const accountQueryVariables = useMemo(() => {
    if (!activeRange.isValid || !activeRange.start || !activeRange.end) {
      return null;
    }

    return {
      created_from: activeRange.start.toISOString(),
      created_to: activeRange.end.toISOString()
    };
  }, [activeRange]);

  const { data: shipmentMetricsData, loading: shipmentMetricsLoading } = useQuery(GET_COMPLETED_SHIPMENTS_BY_TYPE, {
    variables: shipmentQueryVariables || undefined,
    skip: !shipmentQueryVariables,
    fetchPolicy: 'network-only'
  });

  const { data: shipmentAggregateData, loading: shipmentAggregateLoading } = useQuery(GET_COMPLETED_SHIPMENTS_AGGREGATE, {
    variables: shipmentQueryVariables || undefined,
    skip: !shipmentQueryVariables,
    fetchPolicy: 'network-only'
  });

  const {
    data: shipmentDetailsData,
    loading: shipmentDetailsLoading,
    error: shipmentDetailsError
  } = useQuery(GET_COMPLETED_SHIPMENT_DETAILS, {
    variables: shipmentQueryVariables || undefined,
    skip: !shipmentQueryVariables,
    fetchPolicy: 'network-only'
  });

  const {
    data: pickupSlaShipmentsData,
    loading: pickupSlaShipmentsLoading
  } = useQuery(GET_COMPLETED_SHIPMENT_PICKUP_EVENTS, {
    variables: shipmentQueryVariables || undefined,
    skip: !shipmentQueryVariables,
    fetchPolicy: 'network-only'
  });

  const { data: shipmentPayoutsData, loading: shipmentPayoutsLoading } = useQuery(GET_COMPLETED_SHIPMENT_PAYOUTS, {
    variables: shipmentQueryVariables || undefined,
    skip: !shipmentQueryVariables,
    fetchPolicy: 'network-only'
  });

  const weeklyTrendStart = useMemo(() => {
    return resolveTrendRangeStart(activeRange.start, activeRange.end, 'week');
  }, [activeRange.end, activeRange.start]);

  const weeklyTrendEnd = activeRange.end;

  const earliestShipmentDate = useMemo(
    () => resolveEarliestShipmentDate(shipmentAggregateData),
    [shipmentAggregateData]
  );

  const monthlyTrendStart = useMemo(() => {
    if (!activeRange.start) {
      return null;
    }

    if (selectedPreset === 'all_time' && earliestShipmentDate) {
      return startOfMonth(earliestShipmentDate);
    }

    return resolveTrendRangeStart(activeRange.start, activeRange.end, 'year');
  }, [activeRange.start, activeRange.end, earliestShipmentDate, selectedPreset]);

  const monthlyTrendEnd = useMemo(() => {
    if (!activeRange.start || !activeRange.end) {
      return null;
    }

    if (selectedPreset === 'all_time' && earliestShipmentDate) {
      return activeRange.end;
    }

    return resolveTrendRangeEnd(activeRange.start, activeRange.end, 'year');
  }, [activeRange.end, activeRange.start, earliestShipmentDate, selectedPreset]);

  const quarterlyTrendStart = useMemo(() => {
    if (!activeRange.start) {
      return null;
    }

    if (selectedPreset === 'all_time' && earliestShipmentDate) {
      return startOfQuarter(earliestShipmentDate);
    }

    return resolveTrendRangeStart(activeRange.start, activeRange.end, 'year');
  }, [activeRange.end, activeRange.start, earliestShipmentDate, selectedPreset]);

  const quarterlyTrendEnd = useMemo(() => {
    if (!activeRange.start || !activeRange.end) {
      return null;
    }

    if (selectedPreset === 'all_time' && earliestShipmentDate) {
      return activeRange.end;
    }

    return resolveTrendRangeEnd(activeRange.start, activeRange.end, 'year');
  }, [activeRange.end, activeRange.start, earliestShipmentDate, selectedPreset]);

  const trendQueryVariables = useMemo(() => {
    const trendStarts = [weeklyTrendStart, monthlyTrendStart, quarterlyTrendStart]
      .filter(Boolean)
      .sort((left, right) => left.getTime() - right.getTime());
    const trendEnds = [weeklyTrendEnd, monthlyTrendEnd, quarterlyTrendEnd]
      .filter(Boolean)
      .sort((left, right) => right.getTime() - left.getTime());

    if (trendStarts.length === 0 || trendEnds.length === 0) {
      return null;
    }

    return {
      completed_from: trendStarts[0].toISOString(),
      completed_to: trendEnds[0].toISOString()
    };
  }, [monthlyTrendEnd, monthlyTrendStart, quarterlyTrendEnd, quarterlyTrendStart, weeklyTrendEnd, weeklyTrendStart]);

  const { data: trendShipmentData, loading: trendShipmentLoading } = useQuery(GET_COMPLETED_SHIPMENTS_BY_TYPE, {
    variables: trendQueryVariables || undefined,
    skip: !trendQueryVariables,
    fetchPolicy: 'network-only'
  });

  const { data: shipmentDriversData, loading: shipmentDriversLoading } = useQuery(GET_COMPLETED_SHIPMENT_DRIVERS, {
    variables: shipmentQueryVariables || undefined,
    skip: !shipmentQueryVariables,
    fetchPolicy: 'network-only'
  });

  const { data: accountsCreatedData, loading: accountsCreatedLoading } = useQuery(GET_GOPHR_ACCOUNTS_CREATED, {
    variables: accountQueryVariables || undefined,
    skip: !accountQueryVariables,
    fetchPolicy: 'network-only',
    context: { clientName: 'userClient' }
  });

  const { data: accountsCreatedCamelData, loading: accountsCreatedCamelLoading } = useQuery(GET_GOPHR_ACCOUNTS_CREATED_CAMEL, {
    variables: accountQueryVariables || undefined,
    skip: !accountQueryVariables,
    fetchPolicy: 'network-only',
    context: { clientName: 'userClient' }
  });

  const { data: accountsCreatedFallbackData, loading: accountsCreatedFallbackLoading } = useQuery(GET_GOPHR_ACCOUNTS_CREATED, {
    variables: accountQueryVariables || undefined,
    skip: !accountQueryVariables,
    fetchPolicy: 'network-only'
  });

  const { data: accountsCreatedFallbackCamelData, loading: accountsCreatedFallbackCamelLoading } = useQuery(GET_GOPHR_ACCOUNTS_CREATED_CAMEL, {
    variables: accountQueryVariables || undefined,
    skip: !accountQueryVariables,
    fetchPolicy: 'network-only'
  });

  const completedShipments = useMemo(() => {
    const shipmentRows = shipmentMetricsData && Array.isArray(shipmentMetricsData.Shipments)
      ? shipmentMetricsData.Shipments
      : [];
    return shipmentRows;
  }, [shipmentMetricsData]);

  const trendShipments = useMemo(() => {
    const shipmentRows = trendShipmentData && Array.isArray(trendShipmentData.Shipments)
      ? trendShipmentData.Shipments
      : [];
    return shipmentRows;
  }, [trendShipmentData]);

  const completedShipmentDetails = useMemo(() => {
    const shipmentRows = shipmentDetailsData && Array.isArray(shipmentDetailsData.Shipments)
      ? shipmentDetailsData.Shipments
      : [];
    return shipmentRows;
  }, [shipmentDetailsData]);

  const pickupSlaShipments = useMemo(() => {
    const shipmentRows = pickupSlaShipmentsData && Array.isArray(pickupSlaShipmentsData.Shipments)
      ? pickupSlaShipmentsData.Shipments
      : [];
    return shipmentRows;
  }, [pickupSlaShipmentsData]);

  const completedShipmentPayouts = useMemo(() => {
    const shipmentRows = shipmentPayoutsData && Array.isArray(shipmentPayoutsData.Shipments)
      ? shipmentPayoutsData.Shipments
      : [];
    return shipmentRows;
  }, [shipmentPayoutsData]);

  const completedShipmentDrivers = useMemo(() => {
    const routeRows = shipmentDriversData && Array.isArray(shipmentDriversData.Routes)
      ? shipmentDriversData.Routes
      : [];
    return routeRows;
  }, [shipmentDriversData]);

  const totalDeliveryRevenue = useMemo(() => {
    const aggregateRevenue = Number(
      shipmentAggregateData
      && shipmentAggregateData.Shipments_aggregate
      && shipmentAggregateData.Shipments_aggregate.aggregate
      && shipmentAggregateData.Shipments_aggregate.aggregate.sum
        ? shipmentAggregateData.Shipments_aggregate.aggregate.sum.shipping_fee
        : NaN
    );

    if (Number.isFinite(aggregateRevenue)) {
      return aggregateRevenue;
    }

    return completedShipments.reduce((sum, shipment) => {
      const feeValue = Number(shipment && shipment.shipping_fee);
      return sum + (Number.isFinite(feeValue) ? feeValue : 0);
    }, 0);
  }, [completedShipments, shipmentAggregateData]);

  const completedDeliveryCount = useMemo(() => {
    const aggregateCount = Number(
      shipmentAggregateData
      && shipmentAggregateData.Shipments_aggregate
      && shipmentAggregateData.Shipments_aggregate.aggregate
        ? shipmentAggregateData.Shipments_aggregate.aggregate.count
        : NaN
    );

    if (Number.isFinite(aggregateCount)) {
      return aggregateCount;
    }

    return completedShipments.length;
  }, [completedShipments, shipmentAggregateData]);

  const averageOrderValue = useMemo(() => {
    if (completedDeliveryCount <= 0) {
      return 0;
    }

    return totalDeliveryRevenue / completedDeliveryCount;
  }, [completedDeliveryCount, totalDeliveryRevenue]);

  // Counts each completed drop-off leg as one order (so a multidrop shipment counts
  // once per drop), matching the dispatch dashboard's "Total Number of Orders".
  const totalDropOrdersCount = useMemo(() => {
    const isCompletedOrder = order => {
      const actions = order && order.actions && typeof order.actions === 'object' ? order.actions : null;
      return (
        parseTimestampMs(order && order.completed_at)
        || parseTimestampMs(actions && (actions.completed_at || actions.completedAt || actions.dropoff_completed_at || actions.dropoffCompletedAt || actions.pickup_completed_at || actions.pickupCompletedAt))
      ) != null;
    };
    const isDropOffOrder = order => {
      const normalizedAction = String(order && order.actual_action ? order.actual_action : '').trim().toLowerCase();
      const actions = order && order.actions && typeof order.actions === 'object' ? order.actions : null;
      const hasDropOffAction = Boolean(actions && Object.prototype.hasOwnProperty.call(actions, 'drop_off'));
      const hasPickUpAction = Boolean(actions && Object.prototype.hasOwnProperty.call(actions, 'pick_up'));
      return normalizedAction === 'dropoff' || normalizedAction === 'drop_off' || hasDropOffAction || (normalizedAction === '' && !hasPickUpAction);
    };

    return completedShipmentDetails.reduce((sum, shipment) => {
      const orders = Array.isArray(shipment && shipment.Orders) ? shipment.Orders : [];
      return sum + orders.filter(order => isCompletedOrder(order) && isDropOffOrder(order)).length;
    }, 0);
  }, [completedShipmentDetails]);

  const lunchboxRevenue = useMemo(() => completedShipments.reduce((sum, shipment) => {
    if (normalizeShipmentType(shipment && shipment.shipment_type) !== 'school_lunch') {
      return sum;
    }
    const feeValue = Number(shipment && shipment.shipping_fee);
    return sum + (Number.isFinite(feeValue) ? feeValue : 0);
  }, 0), [completedShipments]);

  const marketplaceRevenue = useMemo(() => completedShipments.reduce((sum, shipment) => {
    if (normalizeShipmentType(shipment && shipment.shipment_type) !== 'marketplace') {
      return sum;
    }
    const feeValue = Number(shipment && shipment.shipping_fee);
    return sum + (Number.isFinite(feeValue) ? feeValue : 0);
  }, 0), [completedShipments]);

  const totalDriverPay = useMemo(() => {
    const payoutSourceShipments = completedShipmentPayouts.length > 0
      ? completedShipmentPayouts
      : (completedShipmentDetails.length > 0 ? completedShipmentDetails : completedShipments);

    return payoutSourceShipments.reduce((sum, shipment) => {
      return sum + parseDriverPayFromShippingFeeBlob(shipment && shipment.shipping_fee_blob);
    }, 0);
  }, [completedShipmentDetails, completedShipmentPayouts, completedShipments]);

  const totalGrossProfit = useMemo(
    () => totalDeliveryRevenue - totalDriverPay,
    [totalDeliveryRevenue, totalDriverPay]
  );

  const grossProfitMarginPercent = useMemo(() => {
    if (!Number.isFinite(totalDeliveryRevenue) || totalDeliveryRevenue <= 0) {
      return 0;
    }

    return (totalGrossProfit / totalDeliveryRevenue) * 100;
  }, [totalDeliveryRevenue, totalGrossProfit]);

  const uniqueOrderingCustomersCount = useMemo(() => {
    const uniqueCustomers = new Set();

    completedShipments.forEach(shipment => {
      const customerKey = getUniqueCustomerKey(shipment);
      if (customerKey) {
        uniqueCustomers.add(customerKey);
      }
    });

    return uniqueCustomers.size;
  }, [completedShipments]);

  const uniqueDriversTakingOrdersCount = useMemo(() => {
    const uniqueDrivers = new Set();

    completedShipmentDrivers.forEach(routeRecord => {
      const routeDrivers = Array.isArray(routeRecord && routeRecord.RouteDrivers)
        ? routeRecord.RouteDrivers
        : [];
      routeDrivers.forEach(routeDriver => {
        const driverKey = getUniqueDriverKeyFromRouteDriver(routeDriver);
        if (driverKey) {
          uniqueDrivers.add(driverKey);
        }
      });
    });

    return uniqueDrivers.size;
  }, [completedShipmentDrivers]);

  const shipmentTypeCountBreakdown = useMemo(
    () => buildBreakdown(completedShipments, 'count'),
    [completedShipments]
  );

  const shipmentTypeRevenueBreakdown = useMemo(
    () => buildBreakdown(completedShipments, 'revenue'),
    [completedShipments]
  );

  const vehicleTypeCountBreakdown = useMemo(
    () => buildVehicleBreakdown(completedShipments, 'count'),
    [completedShipments]
  );

  const vehicleTypeRevenueBreakdown = useMemo(
    () => buildVehicleBreakdown(completedShipments, 'revenue'),
    [completedShipments]
  );

  const weeklyTrendSeries = useMemo(() => {
    if (!weeklyTrendStart || !weeklyTrendEnd) {
      return [];
    }
    return buildTimelineSeries(trendShipments, weeklyTrendStart, weeklyTrendEnd, 'week');
  }, [trendShipments, weeklyTrendEnd, weeklyTrendStart]);

  const monthlyTrendSeries = useMemo(() => {
    if (!monthlyTrendStart || !monthlyTrendEnd) {
      return [];
    }
    return buildTimelineSeries(trendShipments, monthlyTrendStart, monthlyTrendEnd, 'month');
  }, [trendShipments, monthlyTrendEnd, monthlyTrendStart]);

  const quarterlyTrendSeries = useMemo(() => {
    if (!quarterlyTrendStart || !quarterlyTrendEnd) {
      return [];
    }
    return buildTimelineSeries(trendShipments, quarterlyTrendStart, quarterlyTrendEnd, 'quarter');
  }, [trendShipments, quarterlyTrendEnd, quarterlyTrendStart]);

  const accountSources = useMemo(() => {
    const usersUserClientSnake = accountsCreatedData && Array.isArray(accountsCreatedData.Users)
      ? accountsCreatedData.Users
      : [];
    const usersUserClientCamel = accountsCreatedCamelData && Array.isArray(accountsCreatedCamelData.Users)
      ? accountsCreatedCamelData.Users
      : [];
    const usersDefaultClientSnake = accountsCreatedFallbackData && Array.isArray(accountsCreatedFallbackData.Users)
      ? accountsCreatedFallbackData.Users
      : [];
    const usersDefaultClientCamel = accountsCreatedFallbackCamelData && Array.isArray(accountsCreatedFallbackCamelData.Users)
      ? accountsCreatedFallbackCamelData.Users
      : [];

    return [
      { source: 'userClient.created_at', users: usersUserClientSnake },
      { source: 'userClient.createdAt', users: usersUserClientCamel },
      { source: 'defaultClient.created_at', users: usersDefaultClientSnake },
      { source: 'defaultClient.createdAt', users: usersDefaultClientCamel }
    ];
  }, [accountsCreatedData, accountsCreatedCamelData, accountsCreatedFallbackData, accountsCreatedFallbackCamelData]);

  const selectedAccountSource = useMemo(() => {
    const nonEmpty = accountSources.find(entry => entry.users.length > 0);
    return nonEmpty || accountSources[0] || { source: 'none', users: [] };
  }, [accountSources]);

  const accountsCreated = selectedAccountSource.users;

  const accountMetricsLoading = accountsCreatedLoading
    || accountsCreatedCamelLoading
    || accountsCreatedFallbackLoading
    || accountsCreatedFallbackCamelLoading;

  const accountTypeBreakdown = useMemo(
    () => buildAccountTypeBreakdown(accountsCreated),
    [accountsCreated]
  );

  const dayOfWeekRows = useMemo(
    () => buildDayOfWeekRows(completedShipments),
    [completedShipments]
  );

  const createdByHourRows = useMemo(
    () => buildHourOfDayRows(completedShipments, 'created'),
    [completedShipments]
  );

  const completedByHourRows = useMemo(
    () => buildHourOfDayRows(completedShipments, 'completed'),
    [completedShipments]
  );

  const expeditedBreakdown = useMemo(
    () => buildExpeditedBreakdown(completedShipmentDetails),
    [completedShipmentDetails]
  );

  const averageDistance = useMemo(() => {
    const validDistances = completedShipmentDetails
      .map(shipment => parseNumericMetric(shipment && shipment.distance))
      .filter(value => Number.isFinite(value) && value >= 0);

    if (validDistances.length === 0) {
      return 0;
    }

    return validDistances.reduce((sum, value) => sum + value, 0) / validDistances.length;
  }, [completedShipmentDetails]);

  const averageWeight = useMemo(() => {
    const validWeights = completedShipmentDetails
      .map(shipment => parseNumericMetric(shipment && shipment.weight))
      .filter(value => Number.isFinite(value) && value >= 0);

    if (validWeights.length === 0) {
      return 0;
    }

    return validWeights.reduce((sum, value) => sum + value, 0) / validWeights.length;
  }, [completedShipmentDetails]);

  const onTimePerformance = useMemo(() => {
    const deliveryGraceWindowMs = ON_TIME_DELIVERY_GRACE_MINUTES * 60 * 1000;
    const pickupExpeditedWindowMs = ON_TIME_PICKUP_EXPEDITED_HOURS * 60 * 60 * 1000;
    const pickupStandardWindowMs = ON_TIME_PICKUP_STANDARD_HOURS * 60 * 60 * 1000;
    const pickupScheduledWindowMs = ON_TIME_PICKUP_SCHEDULED_WINDOW_HOURS * 60 * 60 * 1000;
    const getOrderCompletedMs = order => {
      const actions = order && order.actions && typeof order.actions === 'object' ? order.actions : null;
      return (
        parseTimestampMs(order && order.completed_at)
        || parseTimestampMs(actions && (actions.completed_at || actions.completedAt || actions.dropoff_completed_at || actions.dropoffCompletedAt || actions.pickup_completed_at || actions.pickupCompletedAt))
      );
    };
    const isCompletedMetricOrder = order => getOrderCompletedMs(order) != null;
    let pickupEvaluatedCount = 0;
    let pickupOnTimeCount = 0;
    let deliveryEvaluatedCount = 0;
    let deliveryOnTimeCount = 0;
    let deliverySourceOrderCount = 0;
    let deliveryExcludedDispatchCount = 0;
    let deliveryMissingEtaCount = 0;
    let deliveryMissingCompletionCount = 0;
    const fallbackPickupShipments = completedShipments.filter(shipment => {
      const createdMs = parseTimestampMs(shipment && shipment.created_at);
      return createdMs != null
        && activeRange.start
        && activeRange.end
        && createdMs >= activeRange.start.getTime()
        && createdMs <= activeRange.end.getTime();
    });

    const shipmentsForOnTimePickup = pickupSlaShipments.length > 0 ? pickupSlaShipments : fallbackPickupShipments;
    const shipmentsForOnTimeDelivery = completedShipmentDetails.length > 0 ? completedShipmentDetails : completedShipments;

    shipmentsForOnTimePickup.forEach(shipment => {
      const orders = Array.isArray(shipment && shipment.Orders) ? shipment.Orders : [];
      const completedOrders = orders.filter(isCompletedMetricOrder);
      const pickupOrders = completedOrders.filter(order => {
        const normalizedAction = String(order && order.actual_action ? order.actual_action : '').trim().toLowerCase();
        if (normalizedAction === 'pickup' || normalizedAction === 'pick_up') {
          return true;
        }
        const actions = order && order.actions && typeof order.actions === 'object' ? order.actions : {};
        if (!actions || !actions.pick_up) {
          return false;
        }

        const pickupInstruction = Array.isArray(actions.pick_up)
          ? String(actions.pick_up[0] || '').toLowerCase()
          : String(actions.pick_up).toLowerCase();

        return !pickupInstruction.includes('(drop)');
      });

      const pickupOrdersForTiming = pickupOrders.length > 0
        ? pickupOrders
        : completedOrders.filter(order => {
          const normalizedAction = String(order && order.actual_action ? order.actual_action : '').trim().toLowerCase();
          return normalizedAction !== 'dropoff' && normalizedAction !== 'drop_off';
        });

      const pickupOrdersFallback = pickupOrdersForTiming.length > 0 ? pickupOrdersForTiming : completedOrders;

      const routePath = parseRoutePath(shipment);

      const pickupPathWaypoints = routePath.filter(waypoint => {
        const actions = waypoint && waypoint.actions && typeof waypoint.actions === 'object' ? waypoint.actions : {};
        if (!actions || !actions.pick_up) {
          return false;
        }

        const pickupInstruction = Array.isArray(actions.pick_up)
          ? String(actions.pick_up[0] || '').toLowerCase()
          : String(actions.pick_up).toLowerCase();

        return !pickupInstruction.includes('(drop)');
      });

      // Proper arrival timestamps only (no completed_at fallback here — that shadows path waypoints)
      const pickupTimestampCandidates = pickupOrdersFallback
        .map(order => {
          const arrivedAtMs = parseTimestampMs(order && order.arrived_at);
          if (arrivedAtMs != null) {
            return arrivedAtMs;
          }

          const actions = order && order.actions && typeof order.actions === 'object' ? order.actions : null;
          const arrivalValidationMs = parseTimestampMs(actions && actions.arrival_validation && actions.arrival_validation.timestamp);
          if (arrivalValidationMs != null) {
            return arrivalValidationMs;
          }

          const actionArrivedAtMs = parseTimestampMs(actions && (actions.arrived_at || actions.arrivedAt || actions.driver_arrived_at || actions.driver_arrivedAt));
          if (actionArrivedAtMs != null) {
            return actionArrivedAtMs;
          }

          return null; // Let path waypoints provide the fallback; completed_at used as last resort below
        })
        .filter(value => value != null)
        .sort((left, right) => left - right);

      const pickupPathTimestampCandidates = pickupPathWaypoints
        .map(waypoint => {
          const arrivedAtMs = parseTimestampMs(waypoint && (waypoint.arrived_at || waypoint.arrivedAt));
          if (arrivedAtMs != null) {
            return arrivedAtMs;
          }

          const arrivalValidationMs = parseTimestampMs(waypoint && waypoint.arrival_validation && waypoint.arrival_validation.timestamp);
          if (arrivalValidationMs != null) {
            return arrivalValidationMs;
          }

          return parseTimestampMs(waypoint && (waypoint.completed_at || waypoint.completedAt));
        })
        .filter(value => value != null)
        .sort((left, right) => left - right);

      const pickupActualMs = pickupTimestampCandidates.length > 0
        ? pickupTimestampCandidates[0]
        : pickupPathTimestampCandidates.length > 0
          ? pickupPathTimestampCandidates[0]
          : null;
      const pickupEtaCandidates = pickupOrdersFallback
        .map(order => getOrderEtaMs(order))
        .filter(value => value != null)
        .sort((left, right) => left - right);
      const scheduledMs = parseTimestampMs(shipment && shipment.scheduled_for);
      const shipmentCreatedMs = parseTimestampMs(shipment && shipment.created_at);
      const pickupEtaMs = pickupEtaCandidates.length > 0 ? pickupEtaCandidates[0] : null;
      // Evaluate whenever we have an actual pickup time (order or route-path waypoint)
      // and a rule to measure against (scheduled appointment, order-placement window, or ETA).
      if (pickupActualMs != null && (scheduledMs != null || shipmentCreatedMs != null || pickupEtaMs != null)) {
        let pickupDeadlineMs = null;

        if (scheduledMs != null) {
          pickupDeadlineMs = scheduledMs + pickupScheduledWindowMs;
        } else if (shipmentCreatedMs != null) {
          // Standard/expedited on-demand: 4hr or 1hr window from order placement
          const pickupWindowMs = parseExpeditedFlag(shipment && shipment.is_expedited)
            ? pickupExpeditedWindowMs
            : pickupStandardWindowMs;
          pickupDeadlineMs = shipmentCreatedMs + pickupWindowMs;
        }

        // Also credit drivers who arrived by their committed pickup ETA even when that's later
        // than the on-demand window (e.g. expedited orders the merchant didn't actually want
        // picked up until later). On-time = met the window OR beat the ETA, whichever is later.
        if (pickupEtaMs != null) {
          const etaDeadlineMs = pickupEtaMs + deliveryGraceWindowMs;
          pickupDeadlineMs = pickupDeadlineMs == null ? etaDeadlineMs : Math.max(pickupDeadlineMs, etaDeadlineMs);
        }

        if (pickupDeadlineMs != null) {
          pickupEvaluatedCount += 1;
          if (pickupActualMs <= pickupDeadlineMs) {
            pickupOnTimeCount += 1;
          }
        }
      }
    });

    const hasDispatchCompletionActor = actions => {
      const actorCandidates = [
        actions && actions.completed_by,
        actions && actions.completedBy,
        actions && actions.completed_by_role,
        actions && actions.completedByRole,
        actions && actions.completion_source,
        actions && actions.completionSource,
        actions && actions.actor
      ];

      return actorCandidates.some(candidate => {
        const normalized = String(candidate || '').trim().toLowerCase();
        return normalized.includes('dispatch') || normalized.includes('admin');
      });
    };

    const hasExplicitDispatchArrivalOverride = actions => {
      const arrivalOverride = actions && actions.arrival_override && typeof actions.arrival_override === 'object'
        ? actions.arrival_override
        : null;

      if (!arrivalOverride) {
        return false;
      }

      const reason = String(arrivalOverride.reason || '').trim();
      const actorCandidates = [
        arrivalOverride.created_by,
        arrivalOverride.createdBy,
        arrivalOverride.updated_by,
        arrivalOverride.updatedBy,
        arrivalOverride.actor,
        arrivalOverride.role,
        arrivalOverride.source
      ];

      const hasDispatchActor = actorCandidates.some(candidate => {
        const normalized = String(candidate || '').trim().toLowerCase();
        return normalized.includes('dispatch') || normalized.includes('admin');
      });

      return reason.length > 0 || hasDispatchActor;
    };

    shipmentsForOnTimeDelivery.forEach(shipment => {
      const orders = Array.isArray(shipment && shipment.Orders) ? shipment.Orders : [];
      const completedOrders = orders.filter(isCompletedMetricOrder);

      const eligibleDeliveryOrders = completedOrders.filter(order => {
        const actions = order && order.actions && typeof order.actions === 'object' ? order.actions : null;
        const normalizedAction = String(order && order.actual_action ? order.actual_action : '').trim().toLowerCase();
        const hasDropOffAction = Boolean(actions && Object.prototype.hasOwnProperty.call(actions, 'drop_off'));
        const hasPickUpAction = Boolean(actions && Object.prototype.hasOwnProperty.call(actions, 'pick_up'));
        const isDelivery = (
          normalizedAction === 'dropoff'
          || normalizedAction === 'drop_off'
          || hasDropOffAction
          || (normalizedAction === '' && !hasPickUpAction)
        );

        if (!isDelivery) {
          return false;
        }

        deliverySourceOrderCount += 1;

        const isDispatchMarkedComplete = Boolean(
          hasExplicitDispatchArrivalOverride(actions)
          || (actions && actions.completion_override)
          || hasDispatchCompletionActor(actions)
        );

        if (isDispatchMarkedComplete) {
          deliveryExcludedDispatchCount += 1;
          return false;
        }

        return true;
      });

      eligibleDeliveryOrders.forEach(order => {
        const etaMs = getOrderEtaMs(order);
        const arrivalMs = getOrderArrivalEvidenceMs(shipment, order);

        if (etaMs == null) {
          deliveryMissingEtaCount += 1;
          return;
        }

        deliveryEvaluatedCount += 1;

        if (arrivalMs == null) {
          deliveryOnTimeCount += 1;
          return;
        }

        if (arrivalMs <= (etaMs + deliveryGraceWindowMs)) {
          deliveryOnTimeCount += 1;
          return;
        }

        deliveryMissingCompletionCount += 1;
      });
    });

    // When no pickup has arrival evidence we can't measure on-time pickup, so
    // report N/A rather than a misleading 0% (missing instrumentation != late).
    const pickupRatePercent = pickupEvaluatedCount > 0
      ? (pickupOnTimeCount / pickupEvaluatedCount) * 100
      : null;

    const deliveryRatePercent = deliveryEvaluatedCount > 0
      ? (deliveryOnTimeCount / deliveryEvaluatedCount) * 100
      : null;

    return {
      pickupRatePercent,
      deliveryRatePercent,
      pickupEvaluatedCount,
      pickupOnTimeCount,
      deliveryEvaluatedCount,
      deliveryOnTimeCount,
      pickupSourceCount: shipmentsForOnTimePickup.length,
      deliverySourceOrderCount,
      deliveryExcludedDispatchCount,
      deliveryMissingEtaCount,
      deliveryMissingCompletionCount
    };
  }, [pickupSlaShipments, completedShipmentDetails, completedShipments, activeRange.end, activeRange.start]);

  const pickupOnTimeDisplay = onTimePerformance.pickupRatePercent == null
    ? 'N/A'
    : formatPercent(onTimePerformance.pickupRatePercent || 0);

  const deliveryOnTimeDisplay = onTimePerformance.deliveryRatePercent == null
    ? 'N/A'
    : formatPercent(onTimePerformance.deliveryRatePercent);

  const deliveryOnTimeHint = shipmentDetailsLoading
    ? 'Loading completed delivery timing...'
    : (
      (shipmentDetailsError ? ('Delivery events query error: ' + shipmentDetailsError.message + '. ') : '')
      +
      formatCount(onTimePerformance.deliveryOnTimeCount)
      + ' on time out of '
      + formatCount(onTimePerformance.deliveryEvaluatedCount)
      + ' measured deliveries (ETA + '
      + ON_TIME_DELIVERY_GRACE_MINUTES
      + ' min)'
      + (onTimePerformance.deliveryEvaluatedCount === 0
        ? (
          '. Debug: '
          + formatCount(onTimePerformance.deliverySourceOrderCount)
          + ' delivery orders found, '
          + formatCount(onTimePerformance.deliveryExcludedDispatchCount)
          + ' excluded as dispatch/admin complete, '
          + formatCount(onTimePerformance.deliveryMissingEtaCount)
          + ' missing ETA, '
          + formatCount(onTimePerformance.deliveryMissingCompletionCount)
          + ' missing completion timestamp.'
        )
        : '')
    );

  const rangeDisplay = activeRange.isValid && activeRange.start && activeRange.end
    ? formatDateTime(activeRange.start) + ' -> ' + formatDateTime(activeRange.end)
    : 'Select a valid month or date range.';

  const shipmentToplineLoading = shipmentMetricsLoading || shipmentAggregateLoading;
  const grossProfitLoading = shipmentToplineLoading || shipmentDetailsLoading || shipmentPayoutsLoading;

  return (
    <div className="content">
      <Row>
        <Col md="12">
          <Card>
            <CardHeader>
              <h3 className="title mb-1">Metrics</h3>
              <p className="mb-0" style={{ color: 'rgba(255, 255, 255, 0.92)' }}>One global filter controls every metric on this page.</p>
              <p className="mb-0" style={{ color: 'rgba(255, 255, 255, 0.88)' }}>
                {'Top-line counts and ' + valueLabel.toLowerCase() + ' use shipment aggregates (for all-time accuracy), scoped to shipment_completed (cancelled excluded), using completed_at and falling back to updated_at when needed.'}
              </p>
            </CardHeader>
            <CardBody>
              <Row>
                <Col lg="7" md="12" className="mb-3">
                  <Label className="mb-2">Quick Filters</Label>
                  <div>
                    {PRESET_ROWS.map((row, rowIndex) => (
                      <div
                        key={rowIndex}
                        className="d-flex flex-wrap"
                        style={{ gap: '0.4rem', marginBottom: rowIndex === 0 ? '0.4rem' : 0 }}
                      >
                        {row.map(option => (
                          <Button
                            key={option.key}
                            color={filterMode === 'preset' && selectedPreset === option.key ? 'primary' : 'secondary'}
                            onClick={() => {
                              setFilterMode('preset');
                              setSelectedPreset(option.key);
                            }}
                          >
                            {option.label}
                          </Button>
                        ))}
                      </div>
                    ))}
                  </div>
                </Col>
                <Col lg="2" md="6" className="mb-3">
                  <Label for="metrics-month-picker" className="mb-2">Month Picker</Label>
                  <Input
                    id="metrics-month-picker"
                    type="month"
                    value={selectedMonth}
                    onChange={event => {
                      setSelectedMonth(event.target.value);
                      setFilterMode('month');
                    }}
                  />
                </Col>
                <Col lg="3" md="6" className="mb-3">
                  <Label className="mb-2">Date Range</Label>
                  <div className="d-flex" style={{ gap: '0.4rem' }}>
                    <Input
                      type="date"
                      value={rangeStart}
                      onChange={event => {
                        setRangeStart(event.target.value);
                        setFilterMode('range');
                      }}
                    />
                    <Input
                      type="date"
                      value={rangeEnd}
                      onChange={event => {
                        setRangeEnd(event.target.value);
                        setFilterMode('range');
                      }}
                    />
                  </div>
                </Col>
              </Row>
              <Row>
                <Col md="12" className="d-flex align-items-center" style={{ gap: '0.5rem', flexWrap: 'wrap' }}>
                  <Badge color={activeRange.isValid ? 'success' : 'danger'} pill>
                    {activeRange.label}
                  </Badge>
                  <span style={{ color: 'rgba(255, 255, 255, 0.9)' }}>{rangeDisplay}</span>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col lg={isCustomerView ? '6' : '4'} md="12" className="mb-3">
          <MetricCard
            title="Total Shipments Completed"
            value={shipmentToplineLoading ? 'Loading...' : formatCount(completedDeliveryCount)}
            hint="Completed deliveries in selected filter"
          />
        </Col>
        <Col lg={isCustomerView ? '6' : '4'} md="12" className="mb-3">
          <MetricCard
            title="Total Shipping Fees"
            value={shipmentToplineLoading ? 'Loading...' : formatCurrency(totalDeliveryRevenue)}
            hint={completedDeliveryCount + ' completed deliveries in selected filter'}
          />
        </Col>
        {!isCustomerView && (
          <Col lg="4" md="12" className="mb-3">
            <MetricCard
              title="Total Gross Profit"
              value={grossProfitLoading
                ? 'Loading...'
                : (formatCurrency(totalGrossProfit) + ' (' + formatPercent(grossProfitMarginPercent) + ')')}
              hint="Gross profit and margin as a percentage of shipping fees"
            />
          </Col>
        )}
      </Row>

      <Row>
        <Col lg="4" md="12" className="mb-3">
          <MetricCard
            title="Total Orders (Each Drop)"
            value={shipmentDetailsLoading ? 'Loading...' : formatCount(totalDropOrdersCount)}
            hint="Every completed drop-off counts as one order (multidrop counts per drop)"
          />
        </Col>
        {!isCustomerView && (
          <Col lg="4" md="12" className="mb-3">
            <MetricCard
              title="Lunchbox Revenue"
              value={shipmentMetricsLoading ? 'Loading...' : formatCurrency(lunchboxRevenue)}
              hint="Shipping fees from completed school lunch shipments"
            />
          </Col>
        )}
        {!isCustomerView && (
          <Col lg="4" md="12" className="mb-3">
            <MetricCard
              title="Marketplace Revenue"
              value={shipmentMetricsLoading ? 'Loading...' : formatCurrency(marketplaceRevenue)}
              hint="Shipping fees from completed marketplace shipments"
            />
          </Col>
        )}
      </Row>

      <Row>
        <Col lg="6" md="12" className="mb-3">
          <h5 className="title text-center mb-3" style={{ letterSpacing: '0.03em' }}>SHIPMENTS BY SHIPMENT TYPE</h5>
          <ShipmentTypeDonutCard
            title="Shipments by Shipment Type"
            totalDisplay={formatCount(completedDeliveryCount)}
            breakdownItems={shipmentTypeCountBreakdown}
            valueFormatter={formatCount}
          />
        </Col>

        <Col lg="6" md="12" className="mb-3">
          <h5 className="title text-center mb-3" style={{ letterSpacing: '0.03em' }}>{valueLabel.toUpperCase() + ' BY SHIPMENT TYPE'}</h5>
          <ShipmentTypeDonutCard
            title={valueLabel + ' by Shipment Type'}
            totalDisplay={formatCurrency(totalDeliveryRevenue)}
            breakdownItems={shipmentTypeRevenueBreakdown}
            valueFormatter={formatCurrency}
          />
        </Col>
      </Row>

      <Row>
        <Col lg="4" md="6" sm="12" className="mb-3 mx-auto">
          <MetricCard
            title="Average Order Value"
            value={shipmentToplineLoading ? 'Loading...' : formatCurrency(averageOrderValue)}
            hint="Total shipping fees divided by completed shipments"
          />
        </Col>
      </Row>

      <Row>
        <Col lg="6" md="12" className="mb-3">
          <MetricCard
            title="On-Time Delivery Rate"
            value={shipmentDetailsLoading ? 'Loading...' : deliveryOnTimeDisplay}
            hint={deliveryOnTimeHint}
          />
        </Col>
        <Col lg="6" md="12" className="mb-3">
          <MetricCard
            title="On-Time Pickup Rate"
            value={pickupSlaShipmentsLoading ? 'Loading...' : pickupOnTimeDisplay}
            hint={pickupSlaShipmentsLoading
              ? 'Loading completed pickup timing...'
              : onTimePerformance.pickupEvaluatedCount === 0
                ? ('No pickup arrival timestamps recorded for the ' + formatCount(onTimePerformance.pickupSourceCount) + ' completed shipments in range, so on-time pickup can\u2019t be measured.')
                : (formatCount(onTimePerformance.pickupOnTimeCount) + ' on time out of ' + formatCount(onTimePerformance.pickupEvaluatedCount) + ' measured pickups from ' + formatCount(onTimePerformance.pickupSourceCount) + ' shipments (expedited <= ' + ON_TIME_PICKUP_EXPEDITED_HOURS + 'h from ready/creation, standard <= ' + ON_TIME_PICKUP_STANDARD_HOURS + 'h from ready/creation)')}
          />
        </Col>
      </Row>

      <Row>
        <Col lg="12" md="12" className="mb-2">
          <h5 className="title text-center mb-3" style={{ letterSpacing: '0.03em' }}>{'SHIPMENTS + ' + valueLabel.toUpperCase() + ' TRENDS'}</h5>
        </Col>
        <Col lg="4" md="12" className="mb-3">
          <TimelineChartCard title="Weekly Trend" series={weeklyTrendSeries} loading={trendShipmentLoading} valueLabel={valueLabel} />
        </Col>
        <Col lg="4" md="12" className="mb-3">
          <TimelineChartCard title="Monthly Trend" series={monthlyTrendSeries} loading={trendShipmentLoading} valueLabel={valueLabel} />
        </Col>
        <Col lg="4" md="12" className="mb-3">
          <TimelineChartCard title="Quarterly Trend" series={quarterlyTrendSeries} loading={trendShipmentLoading} valueLabel={valueLabel} />
        </Col>
      </Row>

      <Row>
        <Col lg="12" md="12" className="mb-2">
          <h5 className="title text-center mb-3" style={{ letterSpacing: '0.03em' }}>{'SHIPMENTS + ' + valueLabel.toUpperCase() + ' BY VEHICLE TYPE'}</h5>
        </Col>
        <Col lg="6" md="12" className="mb-3">
          <ShipmentTypeDonutCard
            title="Shipments by Vehicle Type"
            totalDisplay={formatCount(completedDeliveryCount)}
            breakdownItems={vehicleTypeCountBreakdown}
            valueFormatter={formatCount}
          />
        </Col>
        <Col lg="6" md="12" className="mb-3">
          <ShipmentTypeDonutCard
            title={valueLabel + ' by Vehicle Type'}
            totalDisplay={formatCurrency(totalDeliveryRevenue)}
            breakdownItems={vehicleTypeRevenueBreakdown}
            valueFormatter={formatCurrency}
          />
        </Col>
      </Row>

      <Row>
        <Col lg="12" md="12" className="mb-2">
          <h5 className="title text-center mb-3" style={{ letterSpacing: '0.03em' }}>EXPEDITED SHIPMENTS, DISTANCE &amp; WEIGHT</h5>
        </Col>
        <Col lg="3" md="6" sm="12" className="mb-3">
          <GaugeMetricCard
            title="Average Distance (All Shipments)"
            valueDisplay={averageDistance.toFixed(2) + ' mi'}
            unitLabel="Average miles"
            currentValue={averageDistance}
            minValue={0}
            maxValue={100}
            angleOffset={-90}
            ranges={[
              { key: 'distance-low', startAngle: -130, endAngle: -85, color: '#e7b5b8' },
              { key: 'distance-mid', startAngle: -85, endAngle: 5, color: '#f2df8f' },
              { key: 'distance-high', startAngle: 5, endAngle: 130, color: '#a8cf72' }
            ]}
            tickValues={[
              { value: 0, label: '0 mi' },
              { value: 10, label: '10' },
              { value: 50, label: '50' },
              { value: 100, label: '100 mi' }
            ]}
          />
        </Col>
        <Col lg="6" md="12" sm="12" className="mb-3">
          <ShipmentTypeDonutCard
            title="Expedited Shipments"
            totalDisplay={formatCount(completedDeliveryCount)}
            breakdownItems={expeditedBreakdown}
            valueFormatter={formatCount}
          />
        </Col>
        <Col lg="3" md="6" sm="12" className="mb-3">
          <GaugeMetricCard
            title="Average Shipment Weight (pounds)"
            valueDisplay={averageWeight.toFixed(2)}
            unitLabel="Average lbs"
            currentValue={averageWeight}
            minValue={0}
            maxValue={1000}
            angleOffset={-90}
            ranges={[
              { key: 'weight-low', startAngle: -130, endAngle: -25, color: '#f6e08a' },
              { key: 'weight-mid', startAngle: -25, endAngle: 35, color: '#a8cf72' },
              { key: 'weight-high', startAngle: 35, endAngle: 130, color: '#eeb7bc' }
            ]}
            tickValues={[
              { value: 0, label: '0' },
              { value: 150, label: '150' },
              { value: 500, label: '500' },
              { value: 1000, label: '1,000' }
            ]}
          />
        </Col>
      </Row>

      {!isCustomerView && (
        <>
          <Row>
            <Col lg="12" md="12" className="mb-2">
              <h5 className="title text-center mb-3" style={{ letterSpacing: '0.03em' }}>CUSTOMER METRICS</h5>
            </Col>
          </Row>

          <Row>
            <Col lg="4" md="6" sm="12" className="mb-3">
              <MetricCard
                title="Unique Customers Ordering"
                value={shipmentMetricsLoading ? 'Loading...' : formatCount(uniqueOrderingCustomersCount)}
                hint="Distinct customers that placed completed shipments in selected filter"
              />

              <div className="mt-3">
                <MetricCard
                  title="Unique Drivers Took Orders"
                  value={shipmentDriversLoading ? 'Loading...' : formatCount(uniqueDriversTakingOrdersCount)}
                  hint="Distinct drivers assigned to completed shipments in selected filter"
                />
              </div>
            </Col>
            <Col lg="8" md="12" className="mb-3">
              <ShipmentTypeDonutCard
                title="Total Gophr Accounts Created By Type"
                totalDisplay={accountMetricsLoading ? 'Loading...' : formatCount(accountsCreated.length)}
                breakdownItems={accountTypeBreakdown}
                valueFormatter={formatCount}
              />
              {!accountMetricsLoading && (
                <p className="mb-0 mt-1" style={{ fontSize: '0.75rem', textAlign: 'center', color: 'rgba(255, 255, 255, 0.9)' }}>
                  Source: {selectedAccountSource.source}
                </p>
              )}
            </Col>
          </Row>
        </>
      )}

      <Row>
        <Col lg="12" md="12" className="mb-2">
          <h5 className="title text-center mb-3" style={{ letterSpacing: '0.03em' }}>{'SHIPMENTS + ' + valueLabel.toUpperCase() + ' BY DAY OF WEEK'}</h5>
        </Col>
        <Col lg="6" md="12" className="mb-3">
          <DayOfWeekBarChartCard
            title="Shipments by Day of Week"
            rows={dayOfWeekRows}
            valueKey="count"
            color="rgba(138, 212, 223, 0.9)"
            valueFormatter={formatCount}
            axisLabel="Shipments"
          />
        </Col>
        <Col lg="6" md="12" className="mb-3">
          <DayOfWeekBarChartCard
            title={valueLabel + ' by Day of Week'}
            rows={dayOfWeekRows}
            valueKey="revenue"
            color="rgba(143, 200, 79, 0.9)"
            valueFormatter={formatCurrency}
            axisLabel={valueLabel}
          />
        </Col>
      </Row>

      <Row>
        <Col lg="6" md="12" className="mb-3">
          <TimeOfDayLineChartCard
            title="Shipments Created By Time of Day"
            rows={createdByHourRows}
            color="rgba(176, 139, 214, 1)"
            xAxisLabel="created_at: Hour of day"
          />
        </Col>
        <Col lg="6" md="12" className="mb-3">
          <TimeOfDayLineChartCard
            title="Shipments Completed By Time of Day"
            rows={completedByHourRows}
            color="rgba(71, 157, 240, 1)"
            xAxisLabel="completed_at: Hour of day"
          />
        </Col>
      </Row>
    </div>
  );
};

const Metrics = props => {
  const entitlements = useEntitlements();
  const metricsLocked = Boolean(entitlements && entitlements.features && entitlements.features.metrics === false);
  if (metricsLocked) {
    return (
      <div style={{ padding: '40px 20px' }}>
        <Card>
          <CardBody style={{ textAlign: 'center' }}>
            <h4 style={{ color: '#fff' }}>🔒 Metrics is a Pro feature</h4>
            <p style={{ color: '#cdcaca', maxWidth: 520, margin: '8px auto 0' }}>
              Upgrade to Pro to unlock delivery performance reports and analytics dashboards.
            </p>
            <UpgradeHint label="Upgrade to Pro to access Metrics" style={{ fontSize: 14, justifyContent: 'center', display: 'inline-block' }} />
          </CardBody>
        </Card>
      </div>
    );
  }
  return (
    <MetricsPageErrorBoundary>
      <MetricsContent role={props && props.role} />
    </MetricsPageErrorBoundary>
  );
};

export default Metrics;
